import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import OrderInvoice from "components/OrderInvoice";
import { toast } from "react-toastify";
import { License, Order, PaymentMethod, Product, PaymentGateway } from "types";
import { display } from "html2canvas/dist/types/css/property-descriptors/display";
import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { SalesOrderStatusBadge } from "components/SalesOrderstatus";
import PaymentPanel from "components/PaymentOptionsCard";
import SharedOrderDetailsReturn from "components/SalesOrderDetails";
import PaymentMethods from "components/PaymentMethods";
import { safeConsole, createSafeNavigate } from "utils/envUtils";
import {
  fetchPublicGeneralSettingByKey,
  ORDER_JOB_NOTIFICATION_EMAILS_SETTING_KEY,
  parsePublicSettingNotificationEmails,
} from "utils/emailConfig";
import { sendSalesOrderTriggerEmail } from "utils/salesOrderTriggerEmailEngine";
import { getAuthCookie } from "../../utils/authCookies";

function resolveSignagePaymentRecipientLicenseType(licenseType: unknown): "SIGNAGE_FIRM" | "SIGNAGE_TEAM_MEMBER" {
  const s = String(licenseType || "").trim().toUpperCase();
  if (s === "SIGNAGE_TEAM_MEMBER") return "SIGNAGE_TEAM_MEMBER";
  return "SIGNAGE_FIRM";
}
type ProductObj = {
  product_id: string;
  signage_company_id: string;
  product: Product;
  created_by: {
    id: string;
    name: string;
  };
};

type TaxGroup = {
  id: string;
  name: string;
  status: string;
};

type TaxRule = {
  id: string;
  country: string;
  state: string;
  stateCode: string;
  county: string;
  countyCode: string;
  zipCodes: string[];
  taxRate: number;
  comments: string;
  status: string;
};

type StatusHistoryItem = {
  status_name?: string;
  StatusName?: string;
  Old?: number;
  New?: number;
  At?: string | Date;
  old?: number;
  new?: number;
  at?: string | Date | { $date: string };
  type?: string;
  Type?: string;
  note?: string;
  Note?: string;
  by?: {
    id?: string | { $oid: string };
    name?: string;
  };
  By?: {
    id?: string | { $oid: string };
    name?: string;
  };
};

type PaymentInfoItem = {
  payment_id: string;
  total_amount: number;
  amount_paid: number;
  amount_due: number;
  payment_date: string;
  method: string;
};

// const stripePromise = loadStripe(
//   "pk_test_51RIxqdGf4fMlV3rMFokwegpjKwlTB8WUA8jGeH0sg3pnPAelJ0iPOfVXxTDtaIVUD4RjwRMvyWMBwyn2029qowzP00Tp2fcQFU"
// );

const RealtorOrderDetails = () => {
  const SearchData = () => {
    // Implement your search logic here
  };
  const { orderId } = useParams<{ orderId: string }>();
  const [loading, setLoading] = useState(true);
  const token = getAuthCookie("sessionToken");
  const userId = getAuthCookie("userId");
 const user = getAuthCookie("userName");
  const [product, setProduct] = useState<Product>();
  const [productobj, setProductObj] = useState<ProductObj>();
  const [order, setOrder] = useState<Order | null>(null);
  const [customer, setCustomer] = useState<License | null>(null);
  const invoiceRef = useRef<HTMLDivElement>(null);
  const [payment, setPayment] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<{ name: string; _id: string } | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod[]>([]);
  const navigate = useNavigate();
  const safeNavigate = createSafeNavigate(navigate);
  const location = useLocation();
  const isTeammember = location.pathname.includes('/teammember');
  const baseRoute = isTeammember ? '/teammember' : '/realtor';
  const [error, setError] = useState<string>("");
  const [clientSecret, setClientSecret] = useState<string | "">();

  const fetchOrderData = React.useCallback(async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch order data");
        }

        const data = await response.json();
        safeConsole.log("Fetched orders Data:", data);
        if (data) {
          setOrder(data);
        }
      } catch (err) {
        safeConsole.error("Error fetching order data:", err);
      } finally {
        setLoading(false);
      }
    }, [orderId, token]);

  // Fetch orders data
  useEffect(() => {
    if (userId) void fetchOrderData();
  }, [userId, fetchOrderData]);

  const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

  useEffect(() => {
    const fetchIntegrations = async () => {
      
      if (!order?.signage_id) return;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${order?.signage_id}`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch integrations.");
        }
        const data = await response.json();
        safeConsole.log('Methods ', data);
        setIntegrations(data);
      } catch (err) {
        // toast.error("Failed to load integrations.");
        setError(err instanceof Error ? err.message : "An unknown error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchIntegrations();
  }, [token, order?.signage_id]);

  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


  useEffect(() => {
    const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
    safeConsole.log('Stripe info ', stripeInfo);
    if (stripeInfo && stripeInfo.publishable_key) {
      const promise = loadStripe(stripeInfo.publishable_key);
      setStripePromise(promise);
    }
  }, [integrations]);

  const formik = useFormik({
    initialValues: {
      shippingMethod: "",
      paymentMethod: "",
      enable_partial_payment: false,
      partial_amount: 0,
    },
    validationSchema: Yup.object({
      userType: Yup.string().required("Buyer type is required"),
    }),
    onSubmit: async (values) => { },
  });

  useEffect(() => {
    if(!order?.customer_id) return;
    const fetchCustomerData = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/users/${order?.customer_id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (!response.ok) {
          throw new Error("Failed to fetch user data");
        }

        const data = await response.json();
        safeConsole.log("Fetched user Data:", data);
        if (data) {
          setCustomer(data);
        }
      } catch (err) {
        safeConsole.error("Error fetching user data:", err);
      } finally {
        setLoading(false);
      }
    };

    if (orderId) fetchCustomerData();
  }, [orderId, token]);

  const handleDownloadInvoice = async () => {
    if (!invoiceRef.current) return;

    const canvas = await html2canvas(invoiceRef.current);
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const imgWidth = 210;
    const pageHeight = 297;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`Invoice_${order?.order_number}.pdf`);
  };

  const handleOrderStatus = async (ordStatus: number) => {
    try {
      setLoading(true);

      let url = "";
      let body = {};

      // Set API URL and body based on status
      if (ordStatus === 400) {
        url = `${process.env.REACT_APP_API_URL}/orders/${orderId}/cancel`;
      }
      else if (ordStatus === 101) {
        url = `${process.env.REACT_APP_API_URL}/orders/${orderId}/finalize`;
        body = {}
      }

      // Call API
      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: ordStatus === 400 ? null : JSON.stringify(body),
      });

      // Check for failure
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData?.error || "Failed to update order");
      }

      const data = await response.json();
      safeConsole.log("Order Update Success:", data);

      // ✅ Trigger 2: Brokerage Sales Order Shipped - Send notifications (only to end user and supplier)
      if (ordStatus === 301 || data?.status === 301) {
        try {
          const { createNotification } = await import("../../services/notificationApi");
          const { getBrokerageSalesOrderShippedContent } = await import("../../services/notificationContent");
          const { fetchUserData } = await import("../../utils/userDataFetcher");
          
          // Fetch updated order details
          const orderResponse = await fetch(
            `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (orderResponse.ok) {
            const orderData = await orderResponse.json();
            const orderNumber = orderData?.order_number || orderId;

            // Get supplier name
            const supplierId = orderData?.signage_id;
            let supplierName = "Supplier";
            if (supplierId) {
              try {
                const supplierData = await fetchUserData(supplierId, token || "");
                supplierName = supplierData?.companyName || supplierData?.firmDetails?.companyName || supplierData?.firstName || supplierName;
              } catch (err) {
                safeConsole.warn("Failed to fetch supplier data:", err);
              }
            }

            // Determine single end-user recipient (same pattern as RealtorOrderCheckout)
            const endUserId = orderData?.agent_id && orderData?.customer_email 
              ? orderData.agent_id
              : (orderData?.inventory_source === "office" && orderData?.team_member_id 
                ? orderData.team_member_id
                : orderData?.brokerage_firm_id || "");

            // Send notification to single end user only
            if (endUserId && token) {
              try {
                const endUserData = await fetchUserData(endUserId, token || "");
                if (endUserData?.id) {
                  // Determine recipientType based on licenseType
                  let recipientType: "agent" | "brokerage_team" = "agent";
                  if (endUserData.licenseType === "BROKERAGE_FIRM" || endUserData.licenseType === "BROKERAGE_TEAM_MEMBER") {
                    recipientType = "brokerage_team";
                  } else if (endUserData.licenseType === "AGENT" || endUserData.licenseType === "BROKERAGE_AGENT") {
                    recipientType = "agent";
                  }

                  const endUserContent = getBrokerageSalesOrderShippedContent({
                    orderId: orderNumber,
                    supplierName,
                    recipientType: recipientType === "agent" ? "agent" : "brokerage_team_member",
                  });
                  await createNotification(endUserId, endUserContent, token, recipientType === "agent" ? "agent" : "brokerage_team_member");
                }
              } catch (err) {
                safeConsole.warn("Failed to send notification to end user:", err);
              }
            }

            // Notify Supplier (Signage) only
            if (supplierId && token) {
              try {
                const supplierContent = getBrokerageSalesOrderShippedContent({
                  orderId: orderNumber,
                  supplierName,
                  recipientType: "signage_admin",
                });
                await createNotification(supplierId, supplierContent, token, "signage_admin");
              } catch (err) {
                safeConsole.warn("Failed to send notification to supplier:", err);
              }
            }
          }
        } catch (notifError) {
          safeConsole.warn("Failed to send order shipped notification:", notifError);
          // Don't block the status update if notification fails
        }
      }

      // Shipped email now centralized.
      // if (ordStatus === 301 || data?.status === 301) {
      //   try {
      //     if (orderId) {
      //       await sendSalesOrderTriggerEmail({
      //         triggerName: "Brokerage Sales order status update",
      //         orderId,
      //         token: token || "",
      //         salesStatusCode: 301,
      //       });
      //     }
      //   } catch (emailError) {
      //     safeConsole.warn("Failed to send shipped order emails:", emailError);
      //   }
      // }

      safeNavigate(0);
    } catch (err) {
      safeConsole.error("Error in updating order status:", err);
    } finally {
      setLoading(false);
    }
  };

  const statusHistory: StatusHistoryItem[] = Array.isArray(order?.status_history)
    ? (order?.status_history as StatusHistoryItem[])
    : [];
  // console.log("statusHistory", statusHistory);
  const paymentInfo: PaymentInfoItem[] = [...(order?.payment_info || [])];

  const resolveOrderStatus = (status: number | undefined): string => {
    switch (status) {
      case 100: return "Draft";
      case 101: return "Confirmed";
      case 300: return "Shipping";
      case 301: return "Shipped";
      case 302:
      case 401: return "Delivered";
      case 400: return "Cancelled";
      default: return status !== undefined ? `Status ${status}` : "Unknown";
    }
  };

  const parseDate = (input: any): string => {
    try {
      return new Date(input).toLocaleString();
    } catch {
      return "Invalid Date";
    }
  };

  // Build a unified timeline including payment logs
  const getPaymentStatus = (p: PaymentInfoItem) =>
    p.amount_due === 0 ? "Paid" : p.amount_paid > 0 ? "Partial Payment" : "Pending";

  const getPaymentBadgeClass = (status: string) =>
    status === "Paid"
      ? "bg-success-subtle text-success"
      : status === "Partial Payment"
        ? "bg-warning-subtle text-warning"
        : "bg-danger-subtle text-danger";

  const timelineItems = [
    ...(statusHistory || []).map((log) => {
      // Handle date extraction from various formats
      const rawDate = log.At || log.at || new Date();
      let dateValue: string | Date;
      if (typeof rawDate === 'object' && rawDate !== null && '$date' in rawDate) {
        dateValue = (rawDate as { $date: string }).$date;
      } else {
        dateValue = rawDate as string | Date;
      }
      // Handle both PascalCase (By) and camelCase (by) formats
      const byValue = (log as any).By || (log as any).by;
      return {
        type: "status",
        at: dateValue,
        title: `Order #${order?.order_number}`,
        subtitle: `Status: ${resolveOrderStatus(log.New || log.new)}`,
        iconClass: "mdi mdi-truck-fast-outline",
        ...(byValue && { by: byValue }), // Include the by field from status_history only if it exists
      };
    }),
    ...(paymentInfo || []).map((p) => {
      const status = getPaymentStatus(p);
      return {
        type: "payment",
        at: p.payment_date,
        subtitle: (
          <>
            <div>
              <span className="badge bg-success-subtle text-success table-cells">
                ${Number(p.amount_paid || 0).toFixed(2)}{p.method ? ` via ${p.method}` : ""}
              </span>
            </div>
            <div className="mt-1">
              <span className={`badge ${getPaymentBadgeClass(status)}`}>{status}</span>
            </div>
          </>
        ),
        iconClass: "mdi mdi-credit-card-outline",
      };
    }),
  ].sort((a: any, b: any) => new Date(b.at).getTime() - new Date(a.at).getTime());
  const colors = ["#f4c17e", "#fd7e14", "#6f42c1", "#28a745", "#0d6efd"];

  useEffect(() => {
    const fetchPaymentMethods = async () => {
      const id = order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id
      if(!order?.signage_id || !id) {
        safeConsole.error("Missing signage_id or user ID for fetching payment methods");
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/payment-methods/by-supplier/${order?.signage_id}/by-user/${id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        const data = await response.json();
        // console.log("PaymentMethod :", data);
        setPaymentMethod(data);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "An unknown error occurred"
        );
      } finally {
        setLoading(false);
      }
    };
    fetchPaymentMethods();
  }, [order?.inventory_source]);

  useEffect(() => {
    const fetchPaymentIntent = async () => {
      // const amountToPay = (formik.values.partial_amount === 0
      //   ? (Number(order?.total_amount || 0) - totalPaid)
      //   : formik.values.partial_amount);

      safeConsole.log("amountToPay", (formik.values.partial_amount === 0
        ? (order?.payment_info?.length ?? 0) > 0
          ? order?.payment_info[0]?.amount_due
          : order?.total_amount
        : formik.values.partial_amount));

       if (!order?.signage_id) {
        safeConsole.error("Missing signage_id in order");
        return;
      }
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${order?.signage_id}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              // amount: (formik.values.partial_amount === 0
              //   ? (order?.payment_info?.length ?? 0) > 0
              //     ? (Number(order?.payment_info[0]?.amount_due))
              //     : (Number(order?.total_amount))
              //   : formik.values.partial_amount) * 100,
              amount: Math.round(
                        Number(
                          formik.values.partial_amount === 0
                            ? (order?.payment_info?.length ?? 0) > 0
                              ? Number(order?.payment_info?.[0]?.amount_due)
                              : Number(order?.total_amount)
                            : formik.values.partial_amount
                        ) * 100
                      ),

              currency: "usd",
              order_id: String(order?.id || (order as any)?._id || ""),
              order_number: String(order?.order_number || ""),
              customer_name: `${order?.customer_first_name || ""} ${order?.customer_last_name || ""}`.trim(),
              customer_email: String(order?.customer_email || ""),
              customer_phone: String(order?.customer_phone || ""),
              payment_method_types: ["card"],
            }),
          }
        );
        const data = await res.json();
        setClientSecret(data.clientSecret || data.client_secret);
      } catch (err) {
        safeConsole.error("Failed to create PaymentIntent:", err);
      }
    };

    if (stripePromise) fetchPaymentIntent();
  }, [
    stripePromise,
    formik.values.partial_amount,
    order?.total_amount,
    order?.payment_info,
    order?.id,
    order?.order_number,
    order?.customer_first_name,
    order?.customer_last_name,
    order?.customer_email,
    order?.customer_phone,
  ]);


  // Payment email now fully centralized in salesOrderTriggerEmailEngine.ts
  const sendPaymentAppliedEmails = async (
    updatedOrder: any,
    paymentAmountValue: number,
    paymentMethodName: string,
    fromEmail: string
  ) => {
    if (!updatedOrder || !token) {
      safeConsole.warn("sendPaymentAppliedEmails: Missing updatedOrder/token", { updatedOrder: !!updatedOrder, token: !!token });
      return;
    }

    // try {
    //   const resolvedOrderId = String(updatedOrder?.id || (updatedOrder as any)?._id || "").trim();
    //   if (!resolvedOrderId) return;
    //   await sendSalesOrderTriggerEmail({
    //     triggerName: "Signage Payment applied for order",
    //     orderId: resolvedOrderId,
    //     token,
    //     paymentMethodName,
    //     includeOrderPdfAttachment: true,
    //   });
    // } catch (emailError) {
    //   safeConsole.error("Failed to send payment applied emails:", emailError);
    // }
  };

    const completeOrder = async (method: { name: string; _id: string }) => {
      safeConsole.log("method", method);
      setLoading(true);
      let paymentSucceeded = false;
      
      // Find the selected payment method to check is_credit
      const selectedPaymentMethod = paymentMethod.find(pm => pm._id === method._id);
      const isCreditPayment = selectedPaymentMethod?.is_credit || false;

      
      // Credit Payment Logic:
      // - If is_credit: false → User pays immediately → amount_paid: total, amount_due: 0
      // - If is_credit: true → User pays later → amount_paid: 0, amount_due: total
      
      const totalAmount = formik.values.partial_amount === 0
        ? (order?.payment_info?.length ?? 0) > 0
          ? order?.payment_info[0]?.amount_due
          : order?.total_amount
        : formik.values.partial_amount;
  
      try {
        // if (["cheque", "bank_transfer"].includes(getmethod)) {
        //   // 🔁 Call Net30-style API
        //   const latestPayment = order?.payment_info?.[0]?.amount_due;
        //   console.log("latestPayment", latestPayment);
        //   const lastAmountDue = latestPayment ?? totalAmount;
        //   console.log("lastAmountDue", lastAmountDue);
        //   const response = await fetch(
        //     `${process.env.REACT_APP_API_URL}/orders/${order?.id}/net30-payment`,
        //     {
        //       method: "POST",
        //       headers: {
        //         "Content-Type": "application/json",
        //         Authorization: `Bearer ${token}`, // use token from context
        //       },
        //       body: JSON.stringify({
        //         payment_id: formik.values.transactionId || "",
        //         method: getmethod,
        //         amount_paid: formik.values.partial_amount,
        //         total_amount: lastAmountDue,
        //         amount_due: Math.max(0, lastAmountDue - formik.values.partial_amount),
        //         payment_date: new Date().toISOString(),
        //       }),
        //     }
        //   );
  
        //   if (!response.ok) {
        //     throw new Error("Cheque/Bank payment failed.");
        //   }
  
        // } else {
          // 🔁 Default online payment API
          // Calculate payment amounts based on credit status
          const amountPaid = isCreditPayment ? 0 : (totalAmount ?? 0);
          const amountDue = isCreditPayment ? (totalAmount ?? 0) : 0;
          
          
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/orders/${order?.id}/payment`,
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                payment_id: "",
                total_amount: totalAmount,
                amount_paid: amountPaid,
                amount_due: amountDue,
                payment_date: new Date().toISOString(),
                method: method.name,
                paid_by: {
                  id: userId,
                  name: user,
                },
                payment_status: order?.payment_status ?? "",
              }),
            }
          );
  
          if (!response.ok) {
            throw new Error("Online payment failed.");
          }
          
          // Wait a moment for backend to process payment before fetching updated order
          await new Promise(resolve => setTimeout(resolve, 500));
          
          // Fetch updated order to get latest payment info (with retry)
          let updatedOrder = order;
          let retries = 3;
          while (retries > 0) {
            try {
              const updatedOrderRes = await fetch(`${process.env.REACT_APP_API_URL}/orders/${order?.id}`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              if (updatedOrderRes.ok) {
                const fetchedOrder = await updatedOrderRes.json();
                // Check if payment_info has been updated (has at least one payment)
                if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                  updatedOrder = fetchedOrder;
                  break;
                } else if (retries === 1) {
                  // Last retry, use fetched order even if payment_info is empty
                  updatedOrder = fetchedOrder;
                }
              }
            } catch (fetchErr) {
              safeConsole.warn(`Failed to fetch updated order (retries left: ${retries - 1}):`, fetchErr);
            }
            if (retries > 1) {
              await new Promise(resolve => setTimeout(resolve, 500));
            }
            retries--;
          }
          
          // Send "Signage Payment applied for order" emails after successful payment
          const from = process.env.REACT_APP_FROM_EMAIL2;
          if (from) {
            try {
              await sendPaymentAppliedEmails(updatedOrder, amountPaid, method.name, from);
            } catch (emailErr) {
              safeConsole.error("Failed to send payment emails:", emailErr);
              // Don't block payment success, but log the error
            }
          }
        // }

        // Send "Sales order" email notifications after payment completion
        // try {
        //   const from = process.env.REACT_APP_FROM_EMAIL2 || "noreply@realesy.com";
        //   if (from && orderId) {
        //     // Fetch updated order details
        //     const orderResponse = await fetch(
        //       `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
        //       {
        //         method: "GET",
        //         headers: {
        //           Authorization: `Bearer ${token}`,
        //           "Content-Type": "application/json",
        //         },
        //       }
        //     );
            
        //     if (orderResponse.ok) {
        //       const orderData = await orderResponse.json();
        //       const { fetchUserData, fetchMultipleUsersData } = await import("utils/userDataFetcher");
        //       const { sendTriggerEmails } = await import("utils/emailService");
              
        //       // Format order number
        //       const orderNumber = orderData?.order_number || orderId;
              
        //       // Format order date
        //       const orderDate = orderData?.created_at 
        //         ? new Date(orderData.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        //         : new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
              
        //       // Format line items as card-based layout (product name left, quantity right)
        //       const formatLineItems = (items: any[]): string => {
        //         if (!items || items.length === 0) return "<p style='color:#6b7280;font-size:14px;margin:0;'>No items</p>";
                
        //         const itemCards = items.map((item: any, idx: number) => {
        //           const productName = item?.product_name || item?.name || "N/A";
        //           const quantity = item?.quantity || 0;
                  
        //           // Build description from attributes, customizations, or fallback
        //           let description = "";
        //           if (item.attributes && Object.keys(item.attributes).length > 0) {
        //             description = Object.entries(item.attributes)
        //               .map(([k, v]: [string, any]) => {
        //                 const key = k.charAt(0).toUpperCase() + k.slice(1);
        //                 return `${key}: ${v}`;
        //               })
        //               .join(", ");
        //           } else if (item.customizations?.template_name) {
        //             description = item.customizations.template_name;
        //           } else if (item.product_name) {
        //             description = productName;
        //           } else {
        //             description = "Product description not available";
        //           }
                  
        //           return `
        //             <div style="padding:${idx === 0 ? '0' : '16px'} 0 8px 0;border-top:${idx === 0 ? 'none' : '1px solid #e5e7eb'};">
        //               <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px;">
        //                 <div style="flex:1;">
        //                   <div style="font-weight:500;color:#111827;font-size:14px;margin-bottom:4px;line-height:20px;">${productName}</div>
        //                   <div style="color:#6b7280;font-size:12px;margin-bottom:4px;font-style:italic;line-height:18px;">${description}</div>
        //                 </div>
        //                 <div style="color:#6b7280;font-size:12px;line-height:18px;white-space:nowrap;font-weight:500;">Qty: ${quantity}</div>
        //               </div>
        //             </div>
        //           `;
        //         }).join("");
                
        //         return `
        //           <div style="background-color:#ffffff;border-radius:8px;padding:16px;margin:0 0 32px 0;border:1px solid #e5e7eb;">
        //             <h3 style="margin:0 0 16px 0;color:#111827;font-size:18px;font-weight:500;font-family:'Roboto',Arial,sans-serif;">
        //               Line Items
        //             </h3>
        //             ${itemCards}
        //           </div>
        //         `;
        //       };
              
        //       const lineItemsHtml = formatLineItems(orderData?.items || []);
              
        //       // Format products list as text for email
        //       const productsList = (orderData?.items || [])
        //         .map((item: any) => `${item?.product_name || item?.name || "N/A"} (Qty: ${item?.quantity || 0})`)
        //         .join(", ") || "N/A";
              
        //       // Get placed by name
        //       const placedByName = orderData?.customer_first_name && orderData?.customer_last_name
        //         ? `${orderData.customer_first_name} ${orderData.customer_last_name}`
        //         : orderData?.customer_email?.split("@")[0] || user || "User";
              
        //       // Get supplier name
        //       const supplierId = orderData?.signage_id;
        //       let signageCompanyName = "Signage Company";
        //       if (supplierId) {
        //         try {
        //           const supplierData = await fetchUserData(supplierId, token);
        //           signageCompanyName = supplierData?.companyName || supplierData?.firmDetails?.companyName || signageCompanyName;
        //         } catch (err) {
        //           console.warn("Failed to fetch supplier data:", err);
        //         }
        //       }
              
        //       // Format drop-off location
        //       const shippingAddr = orderData?.shipping_address || orderData?.shippingAddress;
        //       const dropOffLocation = shippingAddr
        //         ? `${shippingAddr.addressLine1 || ""}, ${shippingAddr.city || ""}, ${shippingAddr.state || ""} ${shippingAddr.zip || ""}`.trim().replace(/^,\s*|,\s*$/g, "")
        //         : "N/A";
              
        //       // Build base URL
        //       const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "");
        //       const viewOrderUrl = `${baseUrl}${baseRoute}/orders/${orderId}`;
              
        //       // Get brokerage firm name
        //       const brokerageFirmName = orderData?.brokerage_firm_name || "Brokerage Firm";
              
        //       // Track sent emails to avoid duplicates
        //       const sentEmails = new Set<string>();
              
        //       // Send to Brokerage Admin/Team Member
        //       const brokerageIds: string[] = [];
        //       if (orderData?.brokerage_firm_id && !brokerageIds.includes(orderData.brokerage_firm_id)) {
        //         brokerageIds.push(orderData.brokerage_firm_id);
        //       }
        //       if (orderData?.team_member_id && orderData?.team_member_id !== orderData?.brokerage_firm_id && !brokerageIds.includes(orderData.team_member_id)) {
        //         brokerageIds.push(orderData.team_member_id);
        //       }
              
        //       if (brokerageIds.length > 0) {
        //         try {
        //           const brokerageUsersMap = await fetchMultipleUsersData(brokerageIds, token);
        //           const brokerageUsers = Array.from(brokerageUsersMap.values());
                  
        //           for (const brokerageUser of brokerageUsers) {
        //             if (brokerageUser?.email && !sentEmails.has(brokerageUser.email)) {
        //               sentEmails.add(brokerageUser.email);
                      
        //               const emailParams = {
        //                 firstName: brokerageUser.firstName || brokerageUser.email.split("@")[0],
        //                 orderNumber: orderNumber,
        //                 orderDate: orderDate,
        //                 placedBy: placedByName,
        //                 signageCompanyName: signageCompanyName,
        //                 dropOffLocation: dropOffLocation,
        //                 productsOrdered: productsList,
        //                 brokerageFirmName: brokerageFirmName,
        //                 lineItems: lineItemsHtml,
        //                 viewOrderUrl: viewOrderUrl,
        //               };
                      
        //               await sendTriggerEmails(
        //                 "Sales order",
        //                 brokerageUser.licenseType === "BROKERAGE_TEAM_MEMBER" ? "BROKERAGE_TEAM_MEMBER" : "BROKERAGE_FIRM",
        //                 emailParams,
        //                 brokerageUser.email,
        //                 from,
        //                 token || ""
        //               );
        //             }
        //           }
        //         } catch (err) {
        //           console.error("Failed to send emails to brokerage users:", err);
        //         }
        //       }
              
        //       // Send to Supplier (Signage)
        //       if (supplierId) {
        //         try {
        //           const supplierData = await fetchUserData(supplierId, token);
        //           if (supplierData?.email && !sentEmails.has(supplierData.email)) {
        //             sentEmails.add(supplierData.email);
                    
        //             const emailParams = {
        //               supplierName: supplierData.firstName || supplierData.email.split("@")[0],
        //               orderNumber: orderNumber,
        //               orderDate: orderDate,
        //               submittedBy: placedByName,
        //               deliveryLocation: dropOffLocation,
        //               productsRequired: productsList,
        //               brokerageFirmName: brokerageFirmName,
        //               lineItems: lineItemsHtml,
        //               viewOrderUrl: viewOrderUrl,
        //             };
                    
        //             await sendTriggerEmails(
        //               "Sales order",
        //               supplierData.licenseType === "SIGNAGE_TEAM_MEMBER" ? "SIGNAGE_TEAM_MEMBER" : "SIGNAGE_FIRM",
        //               emailParams,
        //               supplierData.email,
        //               from,
        //               token || ""
        //             );
        //           }
        //         } catch (err) {
        //           console.error("Failed to send email to supplier:", err);
        //         }
        //       }
              
        //       // Send to Agent (if applicable)
        //       if (orderData?.agent_id) {
        //         try {
        //           const agentData = await fetchUserData(orderData.agent_id, token);
        //           if (agentData?.email && !sentEmails.has(agentData.email) && agentData.licenseType === "AGENT") {
        //             sentEmails.add(agentData.email);
                    
        //             const emailParams = {
        //               agentFirstName: agentData.firstName || agentData.email.split("@")[0],
        //               orderNumber: orderNumber,
        //               orderDate: orderDate,
        //               deliveryLocation: dropOffLocation,
        //               productsOrdered: productsList,
        //               lineItems: lineItemsHtml,
        //               viewOrderUrl: viewOrderUrl,
        //             };
                    
        //             await sendTriggerEmails(
        //               "Sales order",
        //               "AGENT",
        //               emailParams,
        //               agentData.email,
        //               from,
        //               token || ""
        //             );
        //           }
        //         } catch (err) {
        //           console.error("Failed to send email to agent:", err);
        //         }
        //       }
        //     }
        //   }
        // } catch (emailError) {
        //   console.error("Failed to send payment completion emails:", emailError);
        // }

        toast.success("Payment submitted successfully!");
        // navigate(`${baseRoute}/orders`);
        localStorage.removeItem("cart_id");
        paymentSucceeded = true;
  
      } catch (error) {
        const errMessage =
          (error as Error).message || "An unknown error occurred";
        safeConsole.error("Order Failed:", errMessage);
        toast.error(`Error: ${errMessage}`);
      } finally {
        setLoading(false);
        if (paymentSucceeded) {
          safeNavigate(0);
        }
      }
    };
const autoFinalizedRef = React.useRef(false);

// React.useEffect(() => {
//   if (!autoFinalizedRef.current && order?.status === 100) {
//     autoFinalizedRef.current = true;        
//     handleOrderStatus(101);                
//   }
// }, [order?.status]);  


  const totalPaid = (order?.payment_info ?? []).reduce(
    (acc, payment) => acc + Number(payment.amount_paid || 0),
    0
  );
    const remaingamount = ((order?.total_amount || 0) - totalPaid)
return (
  <SharedOrderDetailsReturn
    login="realtor"
    order={order}
    onRefreshOrder={fetchOrderData}
    onBack={() => safeNavigate(`${baseRoute}/orders`)}
    onDownloadInvoice={handleDownloadInvoice}
    onOrderStatus={handleOrderStatus}
    timelineItems={timelineItems}
    colors={colors}
    parseDate={parseDate}
    baseRoute={baseRoute}
    rightTopPanel={
      order?.payment_status !== "Paid" && order?.status !== 400 ? (
        <PaymentPanel
          variant="realtor"
          // if there is a payment_info row, use amount_due; else use total
          remainingAmount={Number(
            (order?.payment_info?.length ?? 0) > 0
              ? order?.payment_info?.[0]?.amount_due
              : order?.total_amount
          )}
          // remainingAmount={Number(remaingamount)}
          orderStatus={order?.status ?? 0}
          paymentStatus={order?.payment_status ?? ""}
          selectedPaymentMethod={selectedPaymentMethod}
          setSelectedPaymentMethod={setSelectedPaymentMethod}
          formik={formik}               // you already have this in the file
          maxAllowed={Number(
            (order?.payment_info?.length ?? 0) > 0
              ? order?.payment_info?.[0]?.amount_due
              : order?.total_amount ?? 0
          )}
          // dynamicMethods={paymentMethod} // [{ _id, name }]
          onSubmitOffline={(methodKey) => completeOrder(methodKey)}
          paymentInfo={order?.payment_info || []}
          onlinePaymentNode={
            order?.id && stripePromise && clientSecret ? (
              <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
                <PaymentFormManualOrder
                  url="orders"
                  method="PUT"
                  // redirectUrl="/realtor/orders"
                   redirectUrl={
                             `${baseRoute}/orderDetails/${orderId}`
                               
                            }
                  skipRedirectOnSuccess
                  supplierId={order?.signage_id || ""}
                  totalAmount={
                    (order?.payment_info?.length ?? 0) > 0
                      ? order?.payment_info?.[0]?.amount_due
                      : order?.total_amount
                  }
                  paidAmount={
                    formik.values.partial_amount === 0
                      ? (order?.payment_info?.length ?? 0) > 0
                        ? order?.payment_info?.[0]?.amount_due
                        : order?.total_amount
                      : formik.values.partial_amount
                  }
                  orderId={String(order?.id || (order as any)?._id || "")}
                  orderNumber={String(order?.order_number || "")}
                  customerName={`${order?.customer_first_name || ""} ${order?.customer_last_name || ""}`.trim()}
                  customerPhone={String(order?.customer_phone || "")}
                  selectedPaymentMethod={selectedPaymentMethod?.name || "online"}
                  onPaymentSuccess={async (paymentIntentId, updatedOrderFromResponse?: any) => {
                    safeConsole.log("Payment successful:", paymentIntentId);
                    // Don't automatically submit the form - let user decide when to proceed
                    // formik?.handleSubmit?.();
                    
                    // Send "Signage Payment applied for order" emails after successful online payment
                    try {
                      const from = process.env.REACT_APP_FROM_EMAIL2;
                      if (from && order) {
                        safeConsole.log("onPaymentSuccess: Starting email send process for payment:", paymentIntentId);
                        
                        // Use updated order from response if available, otherwise fetch it
                        let updatedOrder = updatedOrderFromResponse || order;
                        
                        // If we don't have updated order from response, fetch it with retry
                        if (!updatedOrderFromResponse) {
                          safeConsole.log("No updated order from response, fetching from API...");
                          // Wait a moment for backend to process payment before fetching updated order
                          await new Promise(resolve => setTimeout(resolve, 1000));
                          
                          let retries = 3;
                          while (retries > 0) {
                            try {
                              const updatedOrderRes = await fetch(`${process.env.REACT_APP_API_URL}/orders/${order.id || (order as any)._id}`, {
                                headers: { Authorization: `Bearer ${token}` },
                              });
                              if (updatedOrderRes.ok) {
                                const fetchedOrder = await updatedOrderRes.json();
                                safeConsole.log("Fetched updated order, payment_info length:", fetchedOrder.payment_info?.length || 0);
                                // Check if payment_info has been updated (has at least one payment)
                                if (Array.isArray(fetchedOrder.payment_info) && fetchedOrder.payment_info.length > 0) {
                                  updatedOrder = fetchedOrder;
                                  safeConsole.log("Payment info found, using updated order");
                                  break;
                                } else if (retries === 1) {
                                  // Last retry, use fetched order even if payment_info is empty
                                  updatedOrder = fetchedOrder;
                                  safeConsole.warn("Using fetched order even though payment_info is empty");
                                }
                              }
                            } catch (fetchErr) {
                              safeConsole.warn(`Failed to fetch updated order for email (retries left: ${retries - 1}):`, fetchErr);
                            }
                            if (retries > 1) {
                              await new Promise(resolve => setTimeout(resolve, 500));
                            }
                            retries--;
                          }
                        } else {
                          safeConsole.log("Using updated order from payment API response");
                        }
                        
                        // Get payment amount from updated order
                        const paymentInfoArray = Array.isArray(updatedOrder.payment_info) ? updatedOrder.payment_info : [];
                        const latestPayment = paymentInfoArray.length > 0 ? paymentInfoArray[paymentInfoArray.length - 1] : null;
                        const paymentAmountValue = latestPayment?.amount_paid 
                          ? Number(latestPayment.amount_paid)
                          : Number(updatedOrder.total_amount || 0);
                        
                        const paymentMethodName = latestPayment?.method || selectedPaymentMethod?.name || "online";
                        
                        safeConsole.log("Calling sendPaymentAppliedEmails with:", {
                          orderId: updatedOrder.id || (updatedOrder as any)._id,
                          paymentAmountValue,
                          paymentMethodName,
                          paymentInfoLength: paymentInfoArray.length
                        });
                        
                        await sendPaymentAppliedEmails(updatedOrder, paymentAmountValue, paymentMethodName, from);
                        
                        safeConsole.log("Email sending completed successfully");
                      } else {
                        safeConsole.warn("onPaymentSuccess: Missing from email or order", { from: !!from, order: !!order });
                      }
                    } catch (emailError) {
                      safeConsole.error("Failed to send payment applied emails:", emailError);
                      // Don't block payment success if email fails, but log the error
                    }
                    localStorage.removeItem("cart_id");
                    safeNavigate(0);
                  }}
                  userId={order?.agent_id}
                  userEmail={order?.customer_email}
                  paidBy_id={userId?.toString() ?? ""}
                  paidBy_name={user ?? ""}
                  payments_status={order?.payment_status ?? ""}
                />
              </Elements>
            ) : null
          }
          paymentMethodsNode={
                      (() => {
                        const userIdForPayment =
                          order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id;
                        return (
                          <PaymentMethods
                            orderId={order?.id || ""}
                            totalAmount={order?.total_amount || 0}
                            supplierId={order?.signage_id || ""}
                            id={userIdForPayment || ""}
                            ordertype="salesOrder"
                            paymentInfo={order?.payment_info || []}
                            /* ⬇️ removed onSubmitOffline prop */
                          />
                        );
                      })()
                    }
        />
      ) : null
    }
  />
);



  // return (
  //   <div className="teammember">
  //     <div className="card">
  //       <div className="card-body">
  //         <h2 className="ps-4">Order Details </h2>
  //       </div>
  //     </div>
  //       <div className="d-flex justify-content-between align-items-center border-bottom pb-3 page-container">
  //             <h4 className="mb-0 fw-bold">Order #{order?.order_number}</h4>
      
  //             <div className="d-flex align-items-center gap-3 flex-wrap">
  //               {/* Order & Payment Status */}
  //               <span>
  //                 <SalesOrderStatusBadge type="order" value={order?.status ?? ""}/>
  //               </span>
  //               <span>
  //                 <SalesOrderStatusBadge type="payment" value={order?.payment_status ?? ""}/>
  //               </span>
       
  //               {/* Dropdown Actions */}
  //               <div className="dropdown">
  //                 <button
  //                   className="btn btn-outline-secondary dropdown-toggle btn-sm br-8 table-cells"
  //                   type="button"
  //                   id="orderActionsDropdown"
  //                   data-bs-toggle="dropdown"
  //                   aria-expanded="false"
  //                 >
  //                   Actions
  //                 </button>
  //                 <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="orderActionsDropdown">
  //                   {/* <Link to={`/signage/invoice/${order?.id}`} className="dropdown-item text-success" target="_blank">
  //                       <i className="ri-download-2-fill me-2"></i>Download Invoice
  //                       </Link> */}
  //                       {(order?.payment_status == "Paid" || (order?.status ?? 0) >= 101) && (
  //                   <li>
  //                     <button className="dropdown-item text-success" onClick={handleDownloadInvoice}>
  //                       <i className="ri-download-2-fill me-2"></i>Download Invoice
  //                     </button>
  //                   </li>
  //                       )}
  //                     {(order?.status ?? 0) < 101 && order?.payment_status !== "Paid" && order?.status !== 400 && (
  //                       <li>
  //                         <Link
  //                           to={`${baseRoute}/order/checkout`}
  //                           state={{ orderId: order?.id }}
  //                           className="dropdown-item text-primary"
  //                         >
  //                           <i className="ri-pencil-fill me-2"></i>Edit
  //                         </Link>
  //                       </li>
  //                     )}
                 
  //                 {/* {order?.status === 100 && (
  //                   <button
  //                     type="button"
  //                     className="dropdown-item text-info"
  //                     onClick={() => handleOrderStatus(101)}
  //                   >
  //                    <i className="mdi mdi-check-bold me-2"></i>Confirm
  //                   </button>
  //                 )} */}
  //                   {order?.status === 100 || order?.payment_status !== "Paid" &&
  //                   order?.status === 101 ? (
  //                   <button
  //                     type="button"
  //                      className="dropdown-item text-danger"
  //                     onClick={() => handleOrderStatus(400)}
  //                   >
  //                     <i className="mdi mdi-archive-remove-outline align-middle me-1"></i>{" "}
  //                      <i className="mdi mdi-archive-remove-outline me-2"></i>Cancel Order
  //                   </button>
  //                 ) : (
  //                   ""
  //                 )}

  //                  {/* {(order?.status === 200 ||
  //                   order?.status === 201 ||
  //                   order?.status === 101 && !(order.payment_status == "Paid")) ? (
  //                   <button
  //                     type="button"
  //                     className="btn btn-success dropdown-item"
  //                     onClick={() => setPayment((prev) => !prev)}
  //                   >
  //                     <i className="mdi mdi-archive-remove-outline align-middle me-1"></i>{" "}
  //                     Process Payment
  //                   </button>
  //                 ) : (
  //                   ""
  //                 )} */}
  //                   {/* {(order?.status ?? 0) > 100 &&
  //                     order?.payment_status !== "Paid" &&
  //                     order?.status !== 400 && (
  //                       <li>
  //                         <button className="dropdown-item text-success" onClick={() => setPayment((prev) => !prev)}>
  //                           <i className="mdi mdi-cash-multiple me-2"></i>Process Payment
  //                         </button>
  //                       </li>
  //                     )} */}
  //                 </ul>
  //               </div>
  //             </div>
  //           </div>
  //     <div className="row">
  //        <div className="col-md-8" id="row-overlap">
  //         <div className="col-md-12 mb-4">
  //           <div className="card h-100">
  //             <div className="card-header">
  //               <h5 className="card-title mb-0 table-headers">
  //                 <i className="ri-user-line align-middle me-2 text-primary fs-18"></i>
  //                 Customer Details
  //               </h5>    </div>
  //             <div className="card-body ms-3">
  //               <div className="row mb-2 ">
  //                 <div className="col-md-4">
  //                   <strong className="table-headers">First Name:</strong><span className="table-cells"> {order?.customer_first_name || "-"}</span>
  //                 </div>
  //                 <div className="col-md-4">
  //                   <strong className="table-headers">Last Name:</strong><span className="table-cells"> {order?.customer_last_name || "-"}</span>
  //                 </div>
  //                 <div className="col-md-4">
  //                   <strong className="table-headers">Email:</strong><span className="table-cells">{order?.customer_email || "-"}</span>
  //                 </div>
  //                 <div className="col-md-4">
  //                   <strong className="table-headers">Phone:</strong><span className="table-cells"> {order?.customer_phone || "-"}</span>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //         <div className="row ms-1">
  //           <div className="col-md-6 mb-4" id="row-overlap">
  //             <div className="card h-100">
  //               <div className="card-header">
  //                 <h5 className="card-title mb-0 table-headers">
  //                   <i className="ri-map-pin-line align-middle me-2 text-primary fs-18"></i>
  //                   Billing Address
  //                 </h5>
  //               </div>

  //               <div className="card-body ms-4">
  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">First Name:</strong>
  //                     <span className="table-cells"> {order?.customer_first_name || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Last Name:</strong>
  //                     <span className="table-cells"> {order?.customer_last_name || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Address Line 1:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.addressLine1 || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Address Line 2:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.addressLine2 || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Phone:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.phone || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">City:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.city || "-"}</span>
  //                   </div>
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">State:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.state || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">Country:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.country || "-"}</span>
  //                   </div>
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">ZIP:</strong>
  //                     <span className="table-cells"> {order?.billing_address?.address_details?.zip || "-"}</span>
  //                   </div>
  //                 </div>
  //               </div>

  //             </div>
  //           </div>
  //           <div className="col-md-6 mb-4" id="row-overlap">
  //             <div className="card h-100">
  //               <div className="card-header">
  //                 <h5 className="card-title mb-0 table-headers">
  //                   <i className="ri-map-pin-line align-middle me-2 text-primary fs-18"></i>Shipping Address
  //                 </h5>
  //               </div>
  //               <div className="card-body ms-4">
  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">First Name:</strong>
  //                     <span className="table-cells"> {order?.customer_first_name || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Last Name:</strong>
  //                     <span className="table-cells"> {order?.customer_last_name || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Address Line 1:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.addressLine1 || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Address Line 2:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.addressLine2 || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-12" id="row-overlap">
  //                     <strong className="table-headers">Phone:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.phone || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">City:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.city || "-"}</span>
  //                   </div>
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">State:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.state || "-"}</span>
  //                   </div>
  //                 </div>

  //                 <div className="row mb-2">
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">Country:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.country || "-"}</span>
  //                   </div>
  //                   <div className="col-md-6" id="row-overlap">
  //                     <strong className="table-headers">ZIP:</strong>
  //                     <span className="table-cells"> {order?.shipping_address?.address_details?.zip || "-"}</span>
  //                   </div>
  //                 </div>
  //               </div>
  //               {/* <div className="card-body ms-3">
  //     <div className="row mb-2">
  //       <div className="col-md-6">
  //         <strong className="table-headers">First Name:</strong>
  //         <span className="table-cells"> { "-"}</span>
  //       </div>
  //       <div className="col-md-6">
  //         <strong className="table-headers">Last Name:</strong>
  //         <span className="table-cells"> { "-"}</span>
  //       </div>
  //     </div>

  //     <div className="row mb-2">
  //        <div className="col-md-6">
  //         <strong className="table-headers">Address Line 1:</strong>
  //         <span className="table-cells"> {order?.shipping_address?.addressLine1 || "-"}</span>
  //       </div>
  //       <div className="col-md-6">
  //         <strong className="table-headers">Address Line 2:</strong>
  //         <span className="table-cells"> {order?.shipping_address?.addressLine2 || "-"}</span>
  //       </div>
  //     </div>

  //     <div className="row mb-2">
  //         <div className="col-md-6">
  //         <strong className="table-headers">Phone:</strong>
  //         <span className="table-cells"> {order?.shipping_address?.phone || "-"}</span>
  //       </div>
  //       <div className="col-md-6">
  //         <strong className="table-headers">City & State:</strong>
  //         <span className="table-cells"> {order?.shipping_address?.city || "-"}, {order?.shipping_address?.state || "-"}</span>
  //       </div>
  //     </div>

  //     <div className="row">
  //        <div className="col-md-6">
  //         <strong className="table-headers">Country:</strong>
  //         <span className="table-cells"> {order?.shipping_address?.country || "-"}</span>
  //       </div>
  //       <div className="col-md-6">
  //         <strong className="table-headers">ZIP:</strong>
  //         <span className="table-cells"> {order?.shipping_address?.zip || "-"}</span>
  //       </div>
  //     </div>
  //   </div> */}
  //             </div>
  //           </div>


  //           {/* <div className="col-md-6 mb-4">
  //             <div className="card h-100">
  //               <div className="card-header">
  //                 <h5 className="card-title mb-0 table-headers">
  //                   <i className="ri-map-pin-line align-middle me-1 text-muted"></i> Billing Address
  //                 </h5>
  //               </div>
  //               <div className="card-body">
  //                 <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
  //                   <li className="fw-medium fs-14 table-cells">{order?.customer_name}</li>
  //                   <li className="table-cells">{order?.billing_address?.phone}</li>
  //                   <li className="table-cells">{order?.billing_address?.addressLine1}</li>
  //                   <li className="table-cells">
  //                     {order?.billing_address?.city}, {order?.billing_address?.state} - {order?.shipping_address?.zip}
  //                   </li>
  //                   <li>{order?.billing_address?.country}</li>
  //                 </ul>
  //               </div>
  //             </div>
  //           </div> */}

  //           {/* <div className="col-md-3 mb-4">
  //             <div className="card h-100">
  //               <div className="card-header">
  //                 <h5 className="card-title mb-0 table-headers">
  //                   <i className="ri-map-pin-line align-middle me-1 text-muted"></i> Shipping Address
  //                 </h5>
  //               </div>
  //               <div className="card-body">
  //                 <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
  //                   <li className="fw-medium fs-14 table-cells">{order?.customer_name}</li>
  //                   <li className="table-cells">{order?.shipping_address?.phone}</li>
  //                   <li className="table-cells">{order?.shipping_address?.addressLine1}</li>
  //                   <li className="table-cells">
  //                     {order?.shipping_address?.city}, {order?.shipping_address?.state} - {order?.shipping_address?.zip}
  //                   </li>
  //                   <li className="table-cells">{order?.shipping_address?.country}</li>
  //                 </ul>
  //               </div>
  //             </div>
  //           </div> */}

  //         </div>
  //         <div className="card ms-1">
  //           <div className="card-body">
  //             <div className="table-responsive table-card" >
  //               <div style={{ maxHeight: "400px", overflowY: "auto" }}>
  //                 <table className="table table-nowrap align-middle table-borderless mb-0 table-fixed">
  //                   <colgroup>
  //                     <col style={{ width: "40%" }} />
  //                     <col style={{ width: "13%" }} />
  //                     <col style={{ width: "13%" }} />
  //                     <col style={{ width: "14%" }} />
  //                   </colgroup>
  //                   <thead className="table-light text-center" style={{ position: "sticky", top: 0, zIndex: 1 }}>
  //                     <tr>
  //                       <th className="table-headers text-center">Product Details</th>
  //                       <th className="table-headers text-center">Sku</th>
  //                       <th className="table-headers text-center">Quantity</th>
  //                       <th className="table-headers text-center">Item Price</th>
  //                       <th className="table-headers text-center">Total Amount</th>
  //                     </tr>
  //                   </thead>
  //                   <tbody>
  //                     {order?.items?.map((item, index) => (
  //                       <tr key={index}>
  //                         <td className="table-cells align-middle" style={{ whiteSpace: "normal", wordWrap: "break-word" }}>
  //                           <div className="d-flex gap-3 align-items-start">
  //                             {/* Product Image */}
  //                             <div style={{ width: "80px", height: "80px", flexShrink: 0 }}>
  //                               <img
  //                                 // src={item.}
  //                                 alt={item?.product_name}
  //                                 className="img-fluid rounded border"
  //                                 style={{ objectFit: "cover", width: "100%", height: "100%" }}
  //                               />
  //                             </div>

  //                             {/* Product Info */}
  //                             <div>
  //                               <h5 className="fs-15 mb-1">
  //                                 <a
  //                                   href={`/signage/viewVariableProduct/${item.product_id}`}
  //                                   className="link-primary"
  //                                 >
  //                                   {item.product_name}
  //                                 </a>
  //                               </h5>
  //                               <div className="text-muted small">
  //                                 {Object.entries(item.attributes || {})?.map(([key, value]) => (
  //                                   <p className="mb-0" key={key}>
  //                                     <strong>{key.charAt(0).toUpperCase() + key.slice(1)}:</strong>{" "}
  //                                     {String(value).trim() || "__"}
  //                                   </p>
  //                                 ))}
  //                               </div>
  //                             </div>
  //                           </div>
  //                         </td>
  //                         <td className="table-cells text-center">{item.sku}</td>
  //                         <td className="table-cells text-center">{item.quantity}</td>
  //                         <td className="table-cells text-center">${item.unit_retail_price}</td>
  //                         <td className="table-cells text-center">${item.total_price.toFixed(2)}</td>
  //                       </tr>
  //                     ))}
  //                   </tbody>
  //                 </table>
  //               </div>
  //             </div>
  //           </div>
  //         </div>
  //         <div className="row">
  //           <div className="col-md-7 mb-3">
  //             <label htmlFor="orderNotes" className="form-label table-cells">
  //               Order Notes
  //             </label>
  //             <textarea
  //               id="orderNotes"
  //               className="form-control"
  //               rows={6}
  //               placeholder="Add any notes related to this order..."
  //             ></textarea>

  //           </div>
  //           <div className="col-md-5 mb-3 d-flex justify-content-end text-end">
  //             <table className="table table-borderless w-auto mb-0 border">
  //               <tbody>
  //                 <tr className="align-middle">
  //                   <td className="table-cells text-center py-1">Sub Total :</td>
  //                   <td className="text-end py-1">
  //                     {`$${(
  //                       Number(order?.total_amount || 0) -
  //                       Number(order?.shipping_amount || 0) -
  //                       Number(order?.tax_amount || 0)
  //                     ).toFixed(2)}`}
  //                   </td>
  //                 </tr>
  //                 {/* <tr className="align-middle">
  //                   <td className="table-cells text-center py-1">Discount :</td>
  //                   <td className="text-end py-1">
  //                     {`-$${Number(order?.discount_amount || 0).toFixed(2)}`}
  //                   </td>
  //                 </tr> */}
  //                 <tr className="align-middle">
  //                   <td className="table-cells text-center py-1">Shipping Charge :</td>
  //                   <td className="text-end py-1">
  //                     {`$${Number(order?.shipping_amount || 0).toFixed(2)}`}
  //                   </td>
  //                 </tr>
  //                 <tr className="align-middle">
  //                   <td className="table-cells text-center py-1">Tax :</td>
  //                   <td className="text-end py-1">
  //                     {`$${Number(order?.tax_amount || 0).toFixed(2)}`}
  //                   </td>
  //                 </tr>
  //                 <tr className="border-top border-top-dashed align-middle">
  //                   <th className="text-center table-headers py-1">Total (USD) :</th>
  //                   <th className="text-end py-1">
  //                     {`$${Number(order?.total_amount || 0).toFixed(2)}`}
  //                   </th>
  //                 </tr>
  //               </tbody>
  //             </table>
  //           </div>
  //           <hr />
  //            <div className="row mb-3 rcust-margin-4">
  //             <div className="col-md-12 d-flex justify-content-between ms-2" id="row-overlap">
  //                 <button
  //                     className="btn btn-outline-secondary br-8 backbutton"
  //                     type="button"
  //                     onClick={() => navigate(-1)}
  //                 >
  //                     Back
  //                 </button>
  //             </div>
  //         </div>

  //           <div className="col-md-12 mt-4">
  //             <h5 className="text-center table-cells mb-4 ms-8">Order Log</h5>
  //             <div className="align-items-center d-flex flex-column timeline">
  //               {timelineItems.map((item: any, idx: number) => {
  //                 const bgColor = colors[idx % colors.length];
  //                 const isLast = idx === timelineItems.length - 1;
  //                 const deliveredClass =
  //                   item.type === "status" &&
  //                   typeof item.subtitle === "string" &&
  //                   item.subtitle.includes("Delivered") &&
  //                   isLast
  //                     ? "delivered-last"
  //                     : "";
  //                 return (
  //                   <div className="gap-4 timeline-item mr-5" key={`${item.type}-${idx}`}>
  //                     <div
  //                       className="px-4 py-3 shadow-sm bg-white text-end"
  //                       style={{
  //                         borderRadius: "10px",
  //                         minWidth: "200px",
  //                         maxWidth: "300px",
  //                         marginRight: "auto",
  //                       }}
  //                     >
  //                       <div className="fw-semibold text-primary table-cells"></div>
  //                       <div className="text-muted small table-cells">
  //                         {typeof item.subtitle === "string" ? item.subtitle : item.subtitle}
  //                       </div>
  //                     </div>
  //                     <div
  //                       className={`timeline-icon rounded-circle d-flex align-items-center justify-content-center ${deliveredClass}`}
  //                       style={{
  //                         width: "40px",
  //                         height: "40px",
  //                         backgroundColor: bgColor,
  //                         color: "#fff",
  //                       }}
  //                     >
  //                       <i className={`${item.iconClass || "mdi mdi-truck-fast-outline"} fs-24`}></i>
  //                     </div>
  //                     <div
  //                       className="text-muted small"
  //                       style={{
  //                         minWidth: "150px",
  //                         textAlign: "start",
  //                         marginLeft: "auto",
  //                       }}
  //                     >
  //                       {parseDate(item.at)}
  //                     </div>
  //                   </div>
  //                 );
  //               })}
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //       { order?.payment_status !== "Paid" &&(
  //       <div className="col-md-4" id="row-overlap">
  //                  <div className="card border shadow-none">
  //                   <div className="card-body">
  //                     <div className="d-flex justify-content-between align-items-center mb-2">
  //                       <h5 className="card-title"><i className="fa-solid fa-money-check-dollar text-success me-2"></i>Payment</h5>
  //                                        <p className="text-danger fw-bold fs-5">
  //                     <i className="fa-solid fa-circle-exclamation me-1"></i> ${order?.total_amount?.toFixed(2)}
  //                   </p>
  //                   </div>
  //                   <div className="dashed-line my-2"></div>
  //                      { order?.id &&  order?.payment_status !== "Paid" && order?.status !== 400 &&(
  //                     <div className="card-body">
  //                       <div className="row mb-3">
  //                         <div className="col-md-12">
  //                           {stripePromise && clientSecret && (
  //                             <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
  //                               <div className="ms-3">
  //                                 <h5 className="mb-1 table-headers">Payment Selection</h5>
  //                                 <p className="text-muted mb-2">
  //                                   Please select payment method
  //                                 </p>
  //                               </div>
        
  //                               {/* Payment Methods */}
  //                               <div className="row">
  //                                 <div className="col-lg-8 col-sm-10">
  //                                   <div className="form-check card-radio">
  //                                     <input
  //                                       id="paymentMethodOnline"
  //                                       name="paymentMethod"
  //                                       type="radio"
  //                                       className="form-check-input"
  //                                       checked={selectedPaymentMethod === "online"}
  //                                       onChange={() =>
  //                                         setSelectedPaymentMethod("online")
  //                                       }
  //                                     />
  //                                     <label
  //                                       className="form-check-label"
  //                                       htmlFor="paymentMethodOnline"
  //                                     >
  //                                       <i className="ri-bank-card-fill align-bottom"></i>
  //                                       <span> Online Payment</span>
  //                                     </label>
  //                                   </div>
  //                                 </div>
  //                               </div>
                                
        
  //                               {/* <div className="row">
  //                                           <div className="col-md-6">
  //                                           <label htmlFor="">Amount to be paid</label>
  //                                           <input
  //                                             type="text"
  //                                             value={totalAmount}
  //                                             readOnly
  //                                             className="form-control"
  //                                           />
  //                                           </div>
  //                                           <div className="col-md-6">
  //                                           <label htmlFor="">Partial amount</label>
  //                                           <input
  //                                             type="text"
  //                                             value={partialAmount}
  //                                             onChange={(e) => setPartialAmount(Number(e.target.value))}
  //                                             className="form-control"
  //                                           />
  //                                             </div>
                                            
  //                                           </div> */}
        
  //                               {selectedPaymentMethod === "online" && (
  //                                 <div className="collapse show mt-4">
  //                                   <div className="">
  //                                     <PaymentFormManualOrder
  //                                       url="orders"
  //                                       method="PUT"
  //                                       redirectUrl={`${baseRoute}/orders`}
  //                                       supplierId={order?.signage_id}
  //                                       totalAmount={
  //                                         (order?.payment_info?.length ?? 0) > 0
  //                                           ? order.payment_info[0]?.amount_due
  //                                           : order?.total_amount
  //                                       }
  //                                       paidAmount={
  //                                         formik.values.partial_amount === 0
  //                                           ? (order?.payment_info?.length ?? 0) > 0
  //                                             ? order.payment_info[0]?.amount_due
  //                                             : order?.total_amount
  //                                           : formik.values.partial_amount
  //                                       }
  //                                       orderId={order?.id}
  //                                       selectedPaymentMethod={selectedPaymentMethod}
  //                                       onPaymentSuccess={(paymentIntentId) => {
  //                                         console.log(
  //                                           "Payment Success: ",
  //                                           paymentIntentId
  //                                         );
  //                                         formik.handleSubmit(); // Proceed to order completion
  //                                       }}
  //                                       userId={order?.agent_id}
  //                                       userEmail={order?.customer_email}
  //                                       paidBy_id={userId?.toString() ?? ""}
  //                                       paidBy_name={user ?? ""}
  //                                       payments_status={order?.payment_status ?? ""}
  //                                     />
  //                                   </div>
  //                                 </div>
  //                               )}
  //                             </Elements>
  //                           )}
  //                            <>
  //                       <div className="">
  //                        {Array.isArray(paymentMethod) &&
  //                              paymentMethod.map((method) => (
  //                           <div className="form-check my-3 mt-3" key={method._id}>
  //                             <input
  //                               className="form-check-input"
  //                               type="radio"
  //                               name="paymentMethod"
  //                               id={`paymentMethod_${method._id}`}
  //                               checked={selectedPaymentMethod === method.name}
  //                               onChange={() => setSelectedPaymentMethod(method.name)}
  //                             />
  //                             <label
  //                               className={`form-check-label ${selectedPaymentMethod === method.name ? "text-success fw-semibold" : ""
  //                                 }`}
  //                               htmlFor={`paymentMethod_${method._id}`}
  //                             >
  //                               {method.name}
  //                             </label>
  //                           </div>
  //                         ))}
  //                       </div>

  //                       {/* ✅ Complete Order button appears only if a method is selected */}
  //                       {selectedPaymentMethod && (
  //                         <div className="d-flex align-items-start gap-3 mt-4">
  //                           <button
  //                             type="button"
  //                             className="btn btn-primary"
  //                             onClick={() => completeOrder(selectedPaymentMethod)}
  //                           >
  //                             Pay With {selectedPaymentMethod}
  //                           </button>
  //                         </div>
  //                       )}
  //                     </>
  //                         </div>
  //                       </div>
  //                     </div>
  //                   )}
  //                   </div>
  //                   </div>
  //                 </div>
  //                 )}
  //     </div>
  //     <div
  //       className="card-body container"
  //       style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
  //     >
  //       <OrderInvoice ref={invoiceRef} order={order} customer={customer} />
  //     </div>
  //   </div>
  // );
};

export default RealtorOrderDetails;














// <div className="col-xl-9">
//           <div className="card">
//             <div className="card-header">
//               <div className="d-flex align-items-center">
//                 <h5 className="card-title flex-grow-1 mb-0 table-cells">
//                   Order #{order?.order_number}
//                 </h5>
//                 <div className="flex-shrink-0">
//                   <button
//                     onClick={handleDownloadInvoice}
//                     className="btn btn-success btn-sm"
//                   >
//                     <i className="ri-download-2-fill align-middle me-1"></i>{" "}
//                     Invoice
//                   </button>
//                 </div>
//               </div>
//             </div>
//             <div className="card-body">
//               <div className="table-responsive table-card">
//                 <table className="table table-nowrap align-middle table-borderless mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th scope="col" className="table-headers text-center">Product Details</th>
//                       <th scope="col" className="table-headers text-center">Item Price</th>
//                       <th scope="col" className="table-headers text-center">Quantity</th>
//                       {/* <th scope="col" className="table-headers text-center">Rating</th> */}
//                       <th scope="col" className="text-end table-headers text-center">Total Amount</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {order?.items?.map((item, index) => (
//                       <tr key={index}>
//                         <td className="table-cells">
//                           <div className="d-flex">
//                             {/* <div className="flex-shrink-0 avatar-md bg-light rounded p-1">
//                   <img
//                     src="assets/images/products/img-8.png"
//                     alt=""
//                     className="img-fluid d-block"
//                   />
//                 </div> */}
//                             <div className="flex-grow-1 ms-3 ">
//                               <h5 className="fs-15 table-cells text-center">
//                                 <a href="#" className="link-primary table-cells text-center">
//                                   {item.product_name}
//                                 </a>
//                               </h5>

//                               {item.customizations && Array.isArray(item.customizations.fields) && item.customizations.fields.length > 0 && (
//                                 <div className="mt-3">
//                                   <p className="fs-15 fw-medium mb-0">Customizations Details:</p>
//                                   <ul className="list-unstyled mt-2 mb-0">
//                                     {item.customizations.fields.map((field, fIndex) => (
//                                       <li key={fIndex} className="mb-1">
//                                         <strong>{field.field_name}:</strong>{' '}
//                                         {field.field_type === "file" && field.value ? (
//                                           <img
//                                             className="border d-block h-50 mt-2 p-1 rounded w-25"
//                                             src={`${process.env.REACT_APP_BASE_URL}${field.value}`}
//                                             alt={field.field_name}
//                                           />
//                                         ) : (
//                                           <span className="fs-14">{field.value || "—"}</span>
//                                         )}
//                                       </li>
//                                     ))}
//                                   </ul>
//                                 </div>
//                               )}
//                             </div>
//                           </div>
//                         </td>
//                         <td className="table-cells text-center">${item.unit_retail_price}</td>
//                         <td className="table-cells text-center">{item.quantity}</td>
//                         {/* <td className="table-cells">
//               <div className="text-warning fs-15">
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-half-fill"></i>
//               </div>
//             </td> */}
//                         <td className="fw-medium text-end table-cells">${item.total_price}</td>
//                       </tr>
//                     ))}
//                     <tr className="border-top border-top-dashed">
//                       <td colSpan={1}></td>
//                       <td colSpan={3} className="fw-medium p-0">
//                         <table className="table table-borderless mb-0 inner-table-collapse">
//                           <tbody>
//                             <tr>
//                               <td className="table-cells py-1">Sub Total :</td>
//                               <td className="text-end table-cells py-1">
//                                 $
//                                 {(
//                                   Number(order?.total_amount) -
//                                   Number(order?.shipping_amount) -
//                                   Number(order?.tax_amount)
//                                 ).toFixed(2)}
//                               </td>
//                             </tr>
//                             <tr>
//                               <td className="table-cells py-1">Discount :</td>
//                               <td className="text-end table-cells py-1">-${order?.discount_amount.toFixed(2)}</td>
//                             </tr>
//                             <tr>
//                               <td className="table-cells py-1">Shipping Charge :</td>
//                               <td className="text-end table-cells py-1">${order?.shipping_amount.toFixed(2)}</td>
//                             </tr>
//                             <tr>
//                               <td className="table-cells py-1">Tax :</td>
//                               <td className="text-end table-cells py-1">${order?.tax_amount.toFixed(2)}</td>
//                             </tr>

//                             <tr className="border-top border-top-dashed">
//                               <th scope="row" className="table-headers py-2">Total (USD) :</th>
//                               <th className="text-end table-headers py-2">${order?.total_amount?.toFixed(2)}</th>
//                             </tr>

//                             {(order?.payment_info ?? []).length > 0 && (
//                               <>
//                                 <tr>
//                                   <td className="table-cells py-1">Already Paid :</td>
//                                   <td className="text-end table-cells py-1">
//                                     {(order?.payment_info ?? []).length > 0
//                                       ? (
//                                         Number(order?.total_amount) -
//                                         Number(
//                                           order?.payment_info[0]?.amount_due
//                                         )
//                                       ).toFixed(2)
//                                       : (0).toFixed(2)}
//                                   </td>
//                                 </tr>
//                                 <tr>
//                                   <td className="table-cells py-1">Remaining Balance:</td>
//                                   <td className="text-end table-cells py-1">
//                                     <strong>{Number(order?.payment_info[0]?.amount_due).toFixed(2)}</strong>
//                                   </td>
//                                 </tr>
//                               </>
//                             )}
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>

//                   </tbody>
//                 </table>
//               </div>
//             </div>

//           </div>

//           <div className="card">
//             <div className="card-header">
//               <div className="d-sm-flex align-items-center">
//                 <div className="d-sm-flex flex-grow-1">
//                   <h5 className="border-black border-end card-title mb-0 pe-2">
//                     Order Status (
//                     {order?.status === 100
//                       ? "Draft"
//                       : order?.status === 101
//                         ? "Confirmed"
//                         : order?.status === 300
//                           ? "Shipping"
//                           : order?.status === 301
//                             ? "Shipped"
//                             : (order?.status === 302 || order?.status === 401)
//                               ? "Delivered"
//                               : order?.status === 400
//                                 ? "Canceled"
//                                 : ""}
//                     )
//                   </h5>
//                   <h5 className="ms-2">Payment Status ({order?.payment_status})</h5>
//                 </div>
//                 <div className="flex-shrink-0 mt-2 mt-sm-0">
//                   {/* <a
//                     href="javascript:void(0);"
//                     className="btn btn-soft-info material-shadow-none btn-sm mt-2 mt-sm-0"
//                   >
//                     <i className="ri-map-pin-line align-middle me-1"></i> Change
//                     Address
//                   </a> */}
                 
//                 </div>
//               </div>
//             </div>
//             {payment && order?.id && (
//               <div className="card-body">
//                 <div className="row mb-3">
//                   <div className="col-md-12">
//                     {stripePromise && clientSecret && (
//                     <Elements stripe={stripePromise} options={{ clientSecret }}>
//                       <div>
//                         <h5 className="mb-1">Payment Selection</h5>
//                         <p className="text-muted mb-2">
//                           Please select payment method
//                         </p>
//                       </div>

//                       {/* Payment Methods */}
//                       <div className="row">
//                         <div className="col-lg-4 col-sm-6">
//                           <div className="form-check card-radio">
//                             <input
//                               id="paymentMethodOnline"
//                               name="paymentMethod"
//                               type="radio"
//                               className="form-check-input"
//                               checked={selectedPaymentMethod === "online"}
//                               onChange={() =>
//                                 setSelectedPaymentMethod("online")
//                               }
//                             />
//                             <label
//                               className="form-check-label"
//                               htmlFor="paymentMethodOnline"
//                             >
//                               <i className="ri-bank-card-fill align-bottom"></i>
//                               <span> Online Payment</span>
//                             </label>
//                           </div>
//                         </div>
//                       </div>

//                       {/* <div className="row">
//                                                 <div className="col-md-6">
//                                                 <label htmlFor="">Amount to be paid</label>
//                                                 <input
//                                                   type="text"
//                                                   value={totalAmount}
//                                                   readOnly
//                                                   className="form-control"
//                                                 />
//                                                 </div>
//                                                 <div className="col-md-6">
//                                                 <label htmlFor="">Partial amount</label>
//                                                 <input
//                                                   type="text"
//                                                   value={partialAmount}
//                                                   onChange={(e) => setPartialAmount(Number(e.target.value))}
//                                                   className="form-control"
//                                                 />
//                                                   </div>
                                                
//                                                 </div> */}

//                       {selectedPaymentMethod === "online" && (
//                         <div className="collapse show mt-4">
//                           <div className="card p-4 border shadow-none">
//                             <PaymentFormManualOrder
//                               url="orders"
//                               method="PUT"
//                               supplierId={order?.signage_id?.toString() || ""}
//                               totalAmount={
//                                 (order?.payment_info?.length ?? 0) > 0
//                                   ? order.payment_info[0]?.amount_due
//                                   : order?.total_amount
//                               }
//                               paidAmount={
//                                 formik.values.partial_amount === 0
//                                   ? (order?.payment_info?.length ?? 0) > 0
//                                     ? order.payment_info[0]?.amount_due
//                                     : order?.total_amount
//                                   : formik.values.partial_amount
//                               }
//                               orderId={order?.id}
//                               selectedPaymentMethod={selectedPaymentMethod}
//                               onPaymentSuccess={(paymentIntentId) => {
//                                 console.log(
//                                   "Payment Success: ",
//                                   paymentIntentId
//                                 );
//                                 formik.handleSubmit(); // Proceed to order completion
//                               }}
//                               userId={order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id}
//                               userEmail={order?.customer_email}
//                             />
//                           </div>
//                         </div>
//                       )}
//                     </Elements>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             )}
//             <div className="card-body">
//               <div className="profile-timeline">
//                 <div
//                   className="accordion accordion-flush"
//                   id="accordionFlushExample"
//                 >
//                   <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingOne">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseOne"
//                         aria-expanded="true"
//                         aria-controls="collapseOne"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-success rounded-circle material-shadow">
//                               <i className="ri-shopping-bag-line"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-15 mb-0 fw-semibold">
//                               Order Placed -{" "}
//                               <span className="fw-normal">
//                                 {order &&
//                                   new Date(order.created_at).toLocaleDateString(
//                                     "en-US"
//                                   )}
//                               </span>
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                     <div
//                       id="collapseOne"
//                       className="accordion-collapse collapse show"
//                       aria-labelledby="headingOne"
//                       data-bs-parent="#accordionExample"
//                     >
//                       <div className="accordion-body ms-2 ps-5 pt-0">
//                         <h6 className="mb-1">An order has been placed.</h6>
//                         <p className="text-muted">
//                           {order &&
//                             new Date(order.created_at).toLocaleDateString(
//                               "en-US"
//                             )}
//                         </p>

//                         <h6 className="mb-1">
//                           Seller has processed your order.
//                         </h6>
//                         <p className="text-muted mb-0">
//                           {order &&
//                             new Date(order.created_at).toLocaleDateString(
//                               "en-US"
//                             )}
//                         </p>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="card mt-4">
//                     <div className="card-header">
//                       <h5 className="card-title mb-0">Order Log</h5>
//                     </div>
//                     <div className="card-body">
//                       <ul className="list-unstyled vstack gap-3">

//                         {/* ✅ Payment Logs with status per entry */}
//                         {paymentInfo.slice().reverse().map((payment, idx) => {
//                           const status =
//                             payment.amount_due === 0
//                               ? "Paid"
//                               : payment.amount_paid > 0
//                                 ? "Partial Payment"
//                                 : "Pending";

//                           const badgeClass =
//                             status === "Paid"
//                               ? "bg-success-subtle text-success"
//                               : status === "Partial Payment"
//                                 ? "bg-warning-subtle text-warning"
//                                 : "bg-danger-subtle text-danger";

//                           return (
//                             <li key={`payment-${idx}`}>
//                               <div>
//                                 <strong>💰 Payment:</strong>
//                                 <span className="badge bg-success-subtle text-success">
//                                   ${payment.amount_paid} {payment.method && `via ${payment.method}`}
//                                 </span>

//                                 <strong className="ms-3">💳 Payment Status:</strong>
//                                 <span className={`badge ${badgeClass}`}>
//                                   {status}
//                                 </span>
//                                 <div className="text-muted small">
//                                   On {parseDate(payment.payment_date)}
//                                 </div>
//                               </div>
//                             </li>
//                           );
//                         })}

//                         {/* ✅ Order Status Logs */}
//                         {statusHistory.slice().map((log, idx) => (
//                           <li key={`status-${idx}`}>
//                             <div>
//                               <strong>📦 Order Status:</strong>{" "}
//                               <span className="badge bg-secondary-subtle text-dark">
//                                 {resolveOrderStatus(log.Old)} → {resolveOrderStatus(log.New)}
//                               </span>
//                               <div className="text-muted small">
//                                 On {parseDate(log.At)}
//                               </div>
//                             </div>
//                           </li>
//                         ))}

//                       </ul>
//                     </div>
//                   </div>

//                   {order?.status === 202 ? (
//                     <div className="accordion-item border-0">
//                       <div className="accordion-header" id="headingTwo">
//                         <a
//                           className="accordion-button p-2 shadow-none"
//                           data-bs-toggle="collapse"
//                           href="#collapseTwo"
//                           aria-expanded="false"
//                           aria-controls="collapseTwo"
//                         >
//                           <div className="d-flex align-items-center">
//                             <div className="flex-shrink-0 avatar-xs">
//                               <div className="avatar-title bg-success rounded-circle material-shadow">
//                                 <i className="mdi mdi-gift-outline"></i>
//                               </div>
//                             </div>
//                             <div className="flex-grow-1 ms-3">
//                               <h6 className="fs-15 mb-1 fw-semibold">
//                                 Paid -{" "}
//                                 <span className="fw-normal">
//                                   {order &&
//                                     new Date(
//                                       order.created_at
//                                     ).toLocaleDateString("en-US")}
//                                 </span>
//                               </h6>
//                             </div>
//                           </div>
//                         </a>
//                       </div>
//                       <div
//                         id="collapseTwo"
//                         className="accordion-collapse collapse show"
//                         aria-labelledby="headingTwo"
//                         data-bs-parent="#accordionExample"
//                       >
//                         <div className="accordion-body ms-2 ps-5 pt-0">
//                           <h6 className="mb-1">
//                             Paid an amount of $
//                             {order?.payment_info?.[0]?.amount_paid}.
//                           </h6>
//                           <p className="text-muted mb-0">
//                             {order?.payment_info?.[0]?.payment_date &&
//                               new Date(
//                                 order?.payment_info?.[0]?.payment_date
//                               ).toLocaleDateString("en-US")}
//                           </p>
//                         </div>
//                       </div>
//                     </div>
//                   ) : (
//                     ""
//                   )}
//                   {/* <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingThree">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseThree"
//                         aria-expanded="false"
//                         aria-controls="collapseThree"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-success rounded-circle material-shadow">
//                               <i className="ri-truck-line"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-15 mb-1 fw-semibold">
//                               Shipping -{" "}
//                               <span className="fw-normal">
//                                 Thu, 16 Dec 2021
//                               </span>
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                     <div
//                       id="collapseThree"
//                       className="accordion-collapse collapse show"
//                       aria-labelledby="headingThree"
//                       data-bs-parent="#accordionExample"
//                     >
//                       <div className="accordion-body ms-2 ps-5 pt-0">
//                         <h6 className="fs-14">
//                           RQK Logistics - MFDS1400457854
//                         </h6>
//                         <h6 className="mb-1">Your item has been shipped.</h6>
//                         <p className="text-muted mb-0">
//                           Sat, 18 Dec 2021 - 4.54PM
//                         </p>
//                       </div>
//                     </div>
//                   </div> */}
//                   {/* <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingFour">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseFour"
//                         aria-expanded="false"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-light text-success rounded-circle material-shadow">
//                               <i className="ri-takeaway-fill"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-14 mb-0 fw-semibold">
//                               Out For Delivery
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                   </div> */}
//                   {/* <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingFive">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseFile"
//                         aria-expanded="false"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-light text-success rounded-circle material-shadow">
//                               <i className="mdi mdi-package-variant"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-14 mb-0 fw-semibold">
//                               Delivered
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                   </div> */}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="col-xl-3">
//           {/* <div className="card">
//             <div className="card-header">
//               <div className="d-flex">
//                 <h5 className="card-title flex-grow-1 mb-0">
//                   <i className="mdi mdi-truck-fast-outline align-middle me-1 text-muted"></i>{" "}
//                   Logistics Details
//                 </h5>
//                 <div className="flex-shrink-0">
//                   <a
//                     href="javascript:void(0);"
//                     className="badge bg-primary-subtle text-primary fs-11"
//                   >
//                     Track Order
//                   </a>
//                 </div>
//               </div>
//             </div>
//             <div className="card-body">
//               <div className="text-center">
//                 <h5 className="fs-16 mt-2">RQK Logistics</h5>
//                 <p className="text-muted mb-0">ID: MFDS1400457854</p>
//                 <p className="text-muted mb-0">Payment Mode : Debit Card</p>
//               </div>
//             </div>
//           </div> */}

//           <div className="card">
//             <div className="card-header">
//               <div className="d-flex">
//                 <h5 className="card-title flex-grow-1 mb-0 table-headers">
//                   Customer Details
//                 </h5>
//               </div>
//             </div>
//             <div className="card-body">
//               <ul className="list-unstyled mb-0 vstack gap-3">
//                 <li>
//                   <div className="d-flex align-items-center">
//                     <div className="flex-grow-1 ms-3">
//                       <h6 className="fs-14 mb-1 table-cells">
//                         {order?.customer_first_name} {order?.customer_last_name}
//                       </h6>
//                       <p className="text-muted mb-0">Customer</p>
//                     </div>
//                   </div>
//                 </li>
//                 <li className="table-cells">
//                   <i className="ri-mail-line me-2 align-middle text-muted fs-16"></i>
//                   {order?.customer_email}
//                 </li>
//                 <li className="table-cells">
//                   <i className="ri-phone-line me-2 align-middle text-muted fs-16"></i>
//                   {order?.customer_phone}
//                 </li>
//               </ul>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <h5 className="card-title mb-0 table-headers">
//                 <i className="ri-map-pin-line align-middle me-1 text-muted"></i>{" "}
//                 Billing Address
//               </h5>
//             </div>
//             <div className="card-body">
//               <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
//                 <li className="fw-medium fs-14 table-cells">{order?.customer_name}</li>
//                 <li className="table-cells">{order?.billing_address?.phone}</li>
//                 <li className="table-cells">{order?.billing_address?.addressLine1}</li>
//                 <li className="table-cells">
//                   {order?.billing_address?.city},{" "}
//                   {order?.billing_address?.state} -{" "}
//                   {order?.shipping_address?.zip}
//                 </li>
//                 <li className="table-cells">{order?.billing_address?.country}</li>
//               </ul>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <h5 className="card-title mb-0 table-headers">
//                 <i className="ri-map-pin-line align-middle me-1 text-muted "></i>{" "}
//                 Shipping Address
//               </h5>
//             </div>
//             <div className="card-body">
//               <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
//                 <li className="fw-medium fs-14 table-cells">{order?.customer_name}</li>
//                 <li className="table-cells">{order?.shipping_address?.phone}</li>
//                 <li className="table-cells">{order?.shipping_address?.addressLine1}</li>
//                 <li className="table-cells">
//                   {order?.shipping_address?.city},{" "}
//                   {order?.shipping_address?.state} -{" "}
//                   {order?.shipping_address?.zip}
//                 </li>
//                 <li className="table-cells">{order?.shipping_address?.country}</li>
//               </ul>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <h5 className="card-title mb-0 table-headers">
//                 <i className="ri-secure-payment-line align-bottom me-1 "></i>{" "}
//                 Payment Details
//               </h5>
//             </div>
//             <div className="card-body">
//               <div className="d-flex align-items-center mb-2">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0 table-cells">Transaction ID:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0 table-cells">
//                     {order?.payment_info?.[0]?.payment_id || ""}
//                   </h6>
//                 </div>
//               </div>
//               <div className="d-flex align-items-center mb-2">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0 table-cells">Payment Method:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0 table-cells">{order?.payment_info?.[0]?.method}</h6>
//                 </div>
//               </div>
//               {/* <div className="d-flex align-items-center mb-2">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0">Card Holder Name:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0">Joseph Parker</h6>
//                 </div>
//               </div> */}
//               <div className="d-flex align-items-center">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0 table-cells">Total Amount:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0 table-cells">${order?.payment_info?.[0]?.total_amount}</h6>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>















// import React, { useEffect, useState, useRef } from "react";
// import { Link, useParams, useNavigate } from "react-router-dom";
// import jsPDF from "jspdf";
// import html2canvas from "html2canvas";
// import OrderInvoice from "components/OrderInvoice";
// import { License } from "types";
// import { Order } from "types";
// import { Product, PaymentGateway } from "types";
// import { display } from "html2canvas/dist/types/css/property-descriptors/display";
// import PaymentFormManualOrder from "../../components/PaymentFormManualOrder";
// import { useFormik } from "formik";
// import * as Yup from "yup";
// import { Elements } from "@stripe/react-stripe-js";
// import { loadStripe, Stripe } from "@stripe/stripe-js";

// type ProductObj = {
//   product_id: string;
//   signage_company_id: string;
//   product: Product;
//   created_by: {
//     id: string;
//     name: string;
//   };
// };

// type TaxGroup = {
//   id: string;
//   name: string;
//   status: string;
// };

// type TaxRule = {
//   id: string;
//   country: string;
//   state: string;
//   stateCode: string;
//   county: string;
//   countyCode: string;
//   zipCodes: string[];
//   taxRate: number;
//   comments: string;
//   status: string;
// };

// type StatusHistoryItem = {
//   status_name: string;
//   Old: number;
//   New: number;
//   At: string | Date;
//   type: string;
//   note?: string;
// };

// type PaymentInfoItem = {
//   payment_id: string;
//   total_amount: number;
//   amount_paid: number;
//   amount_due: number;
//   payment_date: string;
//   method: string;
// };

// // const stripePromise = loadStripe(
// //   "pk_test_51RIxqdGf4fMlV3rMFokwegpjKwlTB8WUA8jGeH0sg3pnPAelJ0iPOfVXxTDtaIVUD4RjwRMvyWMBwyn2029qowzP00Tp2fcQFU"
// // );

// const RealtorOrderDetails = () => {
//   const SearchData = () => {
//     // Implement your search logic here
//   };
//   const { orderId } = useParams<{ orderId: string }>();
//   const [loading, setLoading] = useState(true);
//   const token = getAuthCookie("sessionToken");
//   const userId = getAuthCookie("userId");
//   const [product, setProduct] = useState<Product>();
//   const [productobj, setProductObj] = useState<ProductObj>();
//   const [order, setOrder] = useState<Order | null>(null);
//   const [customer, setCustomer] = useState<License | null>(null);
//   const invoiceRef = useRef<HTMLDivElement>(null);
//   const [payment, setPayment] = useState(false);
//   const [selectedPaymentMethod, setSelectedPaymentMethod] =
//     useState<string>("card");
//   const navigate = useNavigate();
//   const [error, setError] = useState<string>("");
//   const [clientSecret, setClientSecret] = useState<string | "">();

//   // Fetch orders data
//   useEffect(() => {
//     const fetchOrderData = async () => {
//       try {
//         setLoading(true);
//         const response = await fetch(
//           `${process.env.REACT_APP_API_URL}/orders/${orderId}`,
//           {
//             method: "GET",
//             headers: {
//               Authorization: `Bearer ${token}`,
//               "Content-Type": "application/json",
//             },
//           }
//         );
//         if (!response.ok) {
//           throw new Error("Failed to fetch order data");
//         }

//         const data = await response.json();
//         console.log("Fetched orders Data:", data);
//         if (data) {
//           setOrder(data);
//         }
//       } catch (err) {
//         console.error("Error fetching order data:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (userId) fetchOrderData();
//   }, [userId, token]);

//   const [integrations, setIntegrations] = useState<PaymentGateway[]>([]);

//   useEffect(() => {
//     const fetchIntegrations = async () => {
//       try {
//         const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/gateway/created-by/${order?.signage_id}`, {
//           method: "GET",
//           headers: {
//             Authorization: `Bearer ${token}`,
//             "Content-Type": "application/json",
//           },
//         });
//         if (!response.ok) {
//           throw new Error("Failed to fetch integrations.");
//         }
//         const data = await response.json();
//         console.log('Methods ', data);
//         setIntegrations(data);
//       } catch (err) {
//         // toast.error("Failed to load integrations.");
//         setError(err instanceof Error ? err.message : "An unknown error occurred");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchIntegrations();
//   }, [token, order?.signage_id]);

//   const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);


//   useEffect(() => {
//     const stripeInfo = integrations?.find((method) => method.gateway === "stripe");
//     console.log('Stripe info ', stripeInfo);
//     if (stripeInfo && stripeInfo.publishable_key) {
//       const promise = loadStripe(stripeInfo.publishable_key);
//       setStripePromise(promise);
//     }
//   }, [integrations]);

//   const formik = useFormik({
//     initialValues: {
//       shippingMethod: "",
//       paymentMethod: "",
//       enable_partial_payment: false,
//       partial_amount: 0,
//     },
//     validationSchema: Yup.object({
//       userType: Yup.string().required("Buyer type is required"),
//     }),
//     onSubmit: async (values) => { },
//   });

//   useEffect(() => {
//     const fetchCustomerData = async () => {
//       try {
//         setLoading(true);
//         const response = await fetch(
//           `${process.env.REACT_APP_API_URL}/users/${order?.customer_id}`,
//           {
//             method: "GET",
//             headers: {
//               Authorization: `Bearer ${token}`,
//               "Content-Type": "application/json",
//             },
//           }
//         );
//         if (!response.ok) {
//           throw new Error("Failed to fetch user data");
//         }

//         const data = await response.json();
//         console.log("Fetched user Data:", data);
//         if (data) {
//           setCustomer(data);
//         }
//       } catch (err) {
//         console.error("Error fetching user data:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (orderId) fetchCustomerData();
//   }, [orderId, token]);

//   const handleDownloadInvoice = async () => {
//     if (!invoiceRef.current) return;

//     const canvas = await html2canvas(invoiceRef.current);
//     const imgData = canvas.toDataURL("image/png");
//     const pdf = new jsPDF("p", "mm", "a4");
//     const imgWidth = 210;
//     const pageHeight = 297;
//     const imgHeight = (canvas.height * imgWidth) / canvas.width;
//     let heightLeft = imgHeight;
//     let position = 0;

//     pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
//     heightLeft -= pageHeight;

//     while (heightLeft > 0) {
//       position = heightLeft - imgHeight;
//       pdf.addPage();
//       pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
//       heightLeft -= pageHeight;
//     }

//     pdf.save(`Invoice_${order?.order_number}.pdf`);
//   };

//   const handleOrderStatus = async (ordStatus: number) => {
//     try {
//       setLoading(true);

//       let url = "";
//       let body = {};

//       // Set API URL and body based on status
//       if (ordStatus === 400) {
//         url = `${process.env.REACT_APP_API_URL}/orders/${orderId}/cancel`;
//       }
//       else if (ordStatus === 101) {
//         url = `${process.env.REACT_APP_API_URL}/orders/${orderId}/finalize`;
//         body = {}
//       }

//       // Call API
//       const response = await fetch(url, {
//         method: "PUT",
//         headers: {
//           Authorization: `Bearer ${token}`,
//           "Content-Type": "application/json",
//         },
//         body: ordStatus === 400 ? null : JSON.stringify(body),
//       });

//       // Check for failure
//       if (!response.ok) {
//         const errData = await response.json();
//         throw new Error(errData?.error || "Failed to update order");
//       }

//       const data = await response.json();
//       console.log("Order Update Success:", data);

//       navigate(0);
//     } catch (err) {
//       console.error("Error in updating order status:", err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const statusHistory: StatusHistoryItem[] = Array.isArray(order?.status_history)
//     ? (order?.status_history as StatusHistoryItem[])
//     : [];
//   // console.log("statusHistory", statusHistory);
//   const paymentInfo: PaymentInfoItem[] = [...(order?.payment_info || [])];

//   const resolveOrderStatus = (status: number | undefined): string => {
//     switch (status) {
//       case 100: return "Draft";
//       case 101: return "Confirmed";
//       case 300: return "Shipping";
//       case 301: return "Shipped";
//       case 302:
//       case 401: return "Delivered";
//       case 400: return "Cancelled";
//       default: return status !== undefined ? `Status ${status}` : "Unknown";
//     }
//   };

//   const parseDate = (input: any): string => {
//     try {
//       return new Date(input).toLocaleString();
//     } catch {
//       return "Invalid Date";
//     }
//   };

//   useEffect(() => {
//     const fetchPaymentIntent = async () => {
//       // const amountToPay = (formik.values.partial_amount === 0
//       //   ? (Number(order?.total_amount || 0) - totalPaid)
//       //   : formik.values.partial_amount);

//       console.log("amountToPay", (formik.values.partial_amount === 0
//         ? (order?.payment_info?.length ?? 0) > 0
//           ? order?.payment_info[0]?.amount_due
//           : order?.total_amount
//         : formik.values.partial_amount));


//       try {
//         const res = await fetch(
//           `${process.env.REACT_APP_API_URL}/payments/stripe/payment-intent/${order?.signage_id}`,
//           {
//             method: "POST",
//             headers: {
//               Authorization: `Bearer ${token}`,
//               "Content-Type": "application/json",
//             },
//             body: JSON.stringify({
//               amount: (formik.values.partial_amount === 0
//                 ? (order?.payment_info?.length ?? 0) > 0
//                   ? (Number(order?.payment_info[0]?.amount_due))
//                   : (Number(order?.total_amount))
//                 : formik.values.partial_amount) * 100,
//               currency: "usd",
//               payment_method_types: ["card"],
//             }),
//           }
//         );
//         const data = await res.json();
//         setClientSecret(data.clientSecret || data.client_secret);
//       } catch (err) {
//         console.error("Failed to create PaymentIntent:", err);
//       }
//     };

//     if (stripePromise) fetchPaymentIntent();
//   }, [stripePromise, formik.values.partial_amount, order?.total_amount]);

//   return (
//     <div className="teammember">
//       <div className="card">
//         <div className="card-body">
//           <h2 className="ps-4">Order Details </h2>
//         </div>
//       </div>

//       <div className="row">
//         <div className="col-xl-9">
//           <div className="card">
//             <div className="card-header">
//               <div className="d-flex align-items-center">
//                 <h5 className="card-title flex-grow-1 mb-0 table-cells">
//                   Order #{order?.order_number}
//                 </h5>
//                 <div className="flex-shrink-0">
//                   <button
//                     onClick={handleDownloadInvoice}
//                     className="btn btn-success btn-sm"
//                   >
//                     <i className="ri-download-2-fill align-middle me-1"></i>{" "}
//                     Invoice
//                   </button>
//                 </div>
//               </div>
//             </div>
//             <div className="card-body">
//               <div className="table-responsive table-card">
//                 <table className="table table-nowrap align-middle table-borderless mb-0">
//                   <thead className="table-light">
//                     <tr>
//                       <th scope="col" className="table-headers text-center">Product Details</th>
//                       <th scope="col" className="table-headers text-center">Item Price</th>
//                       <th scope="col" className="table-headers text-center">Quantity</th>
//                       {/* <th scope="col" className="table-headers text-center">Rating</th> */}
//                       <th scope="col" className="text-end table-headers text-center">Total Amount</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {order?.items?.map((item, index) => (
//                       <tr key={index}>
//                         <td className="table-cells">
//                           <div className="d-flex">
//                             {/* <div className="flex-shrink-0 avatar-md bg-light rounded p-1">
//                   <img
//                     src="assets/images/products/img-8.png"
//                     alt=""
//                     className="img-fluid d-block"
//                   />
//                 </div> */}
//                             <div className="flex-grow-1 ms-3 ">
//                               <h5 className="fs-15 table-cells text-center">
//                                 <a href="#" className="link-primary table-cells text-center">
//                                   {item.product_name}
//                                 </a>
//                               </h5>

//                               {item.customizations && Array.isArray(item.customizations.fields) && item.customizations.fields.length > 0 && (
//                                 <div className="mt-3">
//                                   <p className="fs-15 fw-medium mb-0">Customizations Details:</p>
//                                   <ul className="list-unstyled mt-2 mb-0">
//                                     {item.customizations.fields.map((field, fIndex) => (
//                                       <li key={fIndex} className="mb-1">
//                                         <strong>{field.field_name}:</strong>{' '}
//                                         {field.field_type === "file" && field.value ? (
//                                           <img
//                                             className="border d-block h-50 mt-2 p-1 rounded w-25"
//                                             src={`${process.env.REACT_APP_BASE_URL}${field.value}`}
//                                             alt={field.field_name}
//                                           />
//                                         ) : (
//                                           <span className="fs-14">{field.value || "—"}</span>
//                                         )}
//                                       </li>
//                                     ))}
//                                   </ul>
//                                 </div>
//                               )}
//                             </div>
//                           </div>
//                         </td>
//                         <td className="table-cells text-center">${item.unit_retail_price}</td>
//                         <td className="table-cells text-center">{item.quantity}</td>
//                         {/* <td className="table-cells">
//               <div className="text-warning fs-15">
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-fill"></i>
//                 <i className="ri-star-half-fill"></i>
//               </div>
//             </td> */}
//                         <td className="fw-medium text-end table-cells">${item.total_price}</td>
//                       </tr>
//                     ))}
//                     <tr className="border-top border-top-dashed">
//                       <td colSpan={1}></td>
//                       <td colSpan={3} className="fw-medium p-0">
//                         <table className="table table-borderless mb-0 inner-table-collapse">
//                           <tbody>
//                             <tr>
//                               <td className="table-cells py-1">Sub Total :</td>
//                               <td className="text-end table-cells py-1">
//                                 $
//                                 {(
//                                   Number(order?.total_amount) -
//                                   Number(order?.shipping_amount) -
//                                   Number(order?.tax_amount)
//                                 ).toFixed(2)}
//                               </td>
//                             </tr>
//                             <tr>
//                               <td className="table-cells py-1">Discount :</td>
//                               <td className="text-end table-cells py-1">-${order?.discount_amount.toFixed(2)}</td>
//                             </tr>
//                             <tr>
//                               <td className="table-cells py-1">Shipping Charge :</td>
//                               <td className="text-end table-cells py-1">${order?.shipping_amount.toFixed(2)}</td>
//                             </tr>
//                             <tr>
//                               <td className="table-cells py-1">Tax :</td>
//                               <td className="text-end table-cells py-1">${order?.tax_amount.toFixed(2)}</td>
//                             </tr>

//                             <tr className="border-top border-top-dashed">
//                               <th scope="row" className="table-headers py-2">Total (USD) :</th>
//                               <th className="text-end table-headers py-2">${order?.total_amount?.toFixed(2)}</th>
//                             </tr>

//                             {(order?.payment_info ?? []).length > 0 && (
//                               <>
//                                 <tr>
//                                   <td className="table-cells py-1">Already Paid :</td>
//                                   <td className="text-end table-cells py-1">
//                                     {(order?.payment_info ?? []).length > 0
//                                       ? (
//                                         Number(order?.total_amount) -
//                                         Number(
//                                           order?.payment_info[0]?.amount_due
//                                         )
//                                       ).toFixed(2)
//                                       : (0).toFixed(2)}
//                                   </td>
//                                 </tr>
//                                 <tr>
//                                   <td className="table-cells py-1">Remaining Balance:</td>
//                                   <td className="text-end table-cells py-1">
//                                     <strong>{Number(order?.payment_info[0]?.amount_due).toFixed(2)}</strong>
//                                   </td>
//                                 </tr>
//                               </>
//                             )}
//                           </tbody>
//                         </table>
//                       </td>
//                     </tr>

//                   </tbody>
//                 </table>
//               </div>
//             </div>

//           </div>

//           <div className="card">
//             <div className="card-header">
//               <div className="d-sm-flex align-items-center">
//                 <div className="d-sm-flex flex-grow-1">
//                   <h5 className="border-black border-end card-title mb-0 pe-2">
//                     Order Status (
//                     {order?.status === 100
//                       ? "Draft"
//                       : order?.status === 101
//                         ? "Confirmed"
//                         : order?.status === 300
//                           ? "Shipping"
//                           : order?.status === 301
//                             ? "Shipped"
//                             : (order?.status === 302 || order?.status === 401)
//                               ? "Delivered"
//                               : order?.status === 400
//                                 ? "Canceled"
//                                 : ""}
//                     )
//                   </h5>
//                   <h5 className="ms-2">Payment Status ({order?.payment_status})</h5>
//                 </div>
//                 <div className="flex-shrink-0 mt-2 mt-sm-0">
//                   {/* <a
//                     href="javascript:void(0);"
//                     className="btn btn-soft-info material-shadow-none btn-sm mt-2 mt-sm-0"
//                   >
//                     <i className="ri-map-pin-line align-middle me-1"></i> Change
//                     Address
//                   </a> */}
//                   {order?.status === 100 ||
//                     order?.status === 101 ? (
//                     <button
//                       type="button"
//                       className="btn btn-danger material-shadow-none btn-sm mt-2 mt-sm-0 me-1"
//                       onClick={() => handleOrderStatus(400)}
//                     >
//                       <i className="mdi mdi-archive-remove-outline align-middle me-1"></i>{" "}
//                       Cancel Order
//                     </button>
//                   ) : (
//                     ""
//                   )}
//                   {(order?.status === 200 ||
//                     order?.status === 201 ||
//                     order?.status === 101 && !(order.payment_status == "Paid")) ? (
//                     <button
//                       type="button"
//                       className="btn btn-success material-shadow-none btn-sm mt-2 mt-sm-0 me-1"
//                       onClick={() => setPayment((prev) => !prev)}
//                     >
//                       <i className="mdi mdi-archive-remove-outline align-middle me-1"></i>{" "}
//                       Process Payment
//                     </button>
//                   ) : (
//                     ""
//                   )}

//                   {order?.status === 100 && (
//                     <button
//                       type="button"
//                       className="btn btn-info material-shadow-none btn-sm mt-2 mt-sm-0 me-1"
//                       onClick={() => handleOrderStatus(101)}
//                     >
//                       <i className="mdi mdi-check-bold align-middle me-1"></i>
//                       Confirm
//                     </button>
//                   )}
//                 </div>
//               </div>
//             </div>
//             {payment && order?.id && (
//               <div className="card-body">
//                 <div className="row mb-3">
//                   <div className="col-md-12">
//                     {stripePromise && clientSecret && (
//                     <Elements stripe={stripePromise} options={{ clientSecret }}>
//                       <div>
//                         <h5 className="mb-1">Payment Selection</h5>
//                         <p className="text-muted mb-2">
//                           Please select payment method
//                         </p>
//                       </div>

//                       {/* Payment Methods */}
//                       <div className="row">
//                         <div className="col-lg-4 col-sm-6">
//                           <div className="form-check card-radio">
//                             <input
//                               id="paymentMethodOnline"
//                               name="paymentMethod"
//                               type="radio"
//                               className="form-check-input"
//                               checked={selectedPaymentMethod === "online"}
//                               onChange={() =>
//                                 setSelectedPaymentMethod("online")
//                               }
//                             />
//                             <label
//                               className="form-check-label"
//                               htmlFor="paymentMethodOnline"
//                             >
//                               <i className="ri-bank-card-fill align-bottom"></i>
//                               <span> Online Payment</span>
//                             </label>
//                           </div>
//                         </div>
//                       </div>

//                       {/* <div className="row">
//                                                 <div className="col-md-6">
//                                                 <label htmlFor="">Amount to be paid</label>
//                                                 <input
//                                                   type="text"
//                                                   value={totalAmount}
//                                                   readOnly
//                                                   className="form-control"
//                                                 />
//                                                 </div>
//                                                 <div className="col-md-6">
//                                                 <label htmlFor="">Partial amount</label>
//                                                 <input
//                                                   type="text"
//                                                   value={partialAmount}
//                                                   onChange={(e) => setPartialAmount(Number(e.target.value))}
//                                                   className="form-control"
//                                                 />
//                                                   </div>
                                                
//                                                 </div> */}

//                       {selectedPaymentMethod === "online" && (
//                         <div className="collapse show mt-4">
//                           <div className="card p-4 border shadow-none">
//                             <PaymentFormManualOrder
//                               url="orders"
//                               method="PUT"
//                               supplierId={order?.signage_id?.toString() || ""}
//                               totalAmount={
//                                 (order?.payment_info?.length ?? 0) > 0
//                                   ? order.payment_info[0]?.amount_due
//                                   : order?.total_amount
//                               }
//                               paidAmount={
//                                 formik.values.partial_amount === 0
//                                   ? (order?.payment_info?.length ?? 0) > 0
//                                     ? order.payment_info[0]?.amount_due
//                                     : order?.total_amount
//                                   : formik.values.partial_amount
//                               }
//                               orderId={order?.id}
//                               selectedPaymentMethod={selectedPaymentMethod}
//                               onPaymentSuccess={(paymentIntentId) => {
//                                 console.log(
//                                   "Payment Success: ",
//                                   paymentIntentId
//                                 );
//                                 formik.handleSubmit(); // Proceed to order completion
//                               }}
//                               userId={order?.inventory_source === "office" ? order?.team_member_id : order?.agent_id}
//                               userEmail={order?.customer_email}
//                             />
//                           </div>
//                         </div>
//                       )}
//                     </Elements>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             )}
//             <div className="card-body">
//               <div className="profile-timeline">
//                 <div
//                   className="accordion accordion-flush"
//                   id="accordionFlushExample"
//                 >
//                   <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingOne">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseOne"
//                         aria-expanded="true"
//                         aria-controls="collapseOne"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-success rounded-circle material-shadow">
//                               <i className="ri-shopping-bag-line"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-15 mb-0 fw-semibold">
//                               Order Placed -{" "}
//                               <span className="fw-normal">
//                                 {order &&
//                                   new Date(order.created_at).toLocaleDateString(
//                                     "en-US"
//                                   )}
//                               </span>
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                     <div
//                       id="collapseOne"
//                       className="accordion-collapse collapse show"
//                       aria-labelledby="headingOne"
//                       data-bs-parent="#accordionExample"
//                     >
//                       <div className="accordion-body ms-2 ps-5 pt-0">
//                         <h6 className="mb-1">An order has been placed.</h6>
//                         <p className="text-muted">
//                           {order &&
//                             new Date(order.created_at).toLocaleDateString(
//                               "en-US"
//                             )}
//                         </p>

//                         <h6 className="mb-1">
//                           Seller has processed your order.
//                         </h6>
//                         <p className="text-muted mb-0">
//                           {order &&
//                             new Date(order.created_at).toLocaleDateString(
//                               "en-US"
//                             )}
//                         </p>
//                       </div>
//                     </div>
//                   </div>

//                   <div className="card mt-4">
//                     <div className="card-header">
//                       <h5 className="card-title mb-0">Order Log</h5>
//                     </div>
//                     <div className="card-body">
//                       <ul className="list-unstyled vstack gap-3">

//                         {/* ✅ Payment Logs with status per entry */}
//                         {paymentInfo.slice().reverse().map((payment, idx) => {
//                           const status =
//                             payment.amount_due === 0
//                               ? "Paid"
//                               : payment.amount_paid > 0
//                                 ? "Partial Payment"
//                                 : "Pending";

//                           const badgeClass =
//                             status === "Paid"
//                               ? "bg-success-subtle text-success"
//                               : status === "Partial Payment"
//                                 ? "bg-warning-subtle text-warning"
//                                 : "bg-danger-subtle text-danger";

//                           return (
//                             <li key={`payment-${idx}`}>
//                               <div>
//                                 <strong>💰 Payment:</strong>
//                                 <span className="badge bg-success-subtle text-success">
//                                   ${payment.amount_paid} {payment.method && `via ${payment.method}`}
//                                 </span>

//                                 <strong className="ms-3">💳 Payment Status:</strong>
//                                 <span className={`badge ${badgeClass}`}>
//                                   {status}
//                                 </span>
//                                 <div className="text-muted small">
//                                   On {parseDate(payment.payment_date)}
//                                 </div>
//                               </div>
//                             </li>
//                           );
//                         })}

//                         {/* ✅ Order Status Logs */}
//                         {statusHistory.slice().map((log, idx) => (
//                           <li key={`status-${idx}`}>
//                             <div>
//                               <strong>📦 Order Status:</strong>{" "}
//                               <span className="badge bg-secondary-subtle text-dark">
//                                 {resolveOrderStatus(log.Old)} → {resolveOrderStatus(log.New)}
//                               </span>
//                               <div className="text-muted small">
//                                 On {parseDate(log.At)}
//                               </div>
//                             </div>
//                           </li>
//                         ))}

//                       </ul>
//                     </div>
//                   </div>

//                   {order?.status === 202 ? (
//                     <div className="accordion-item border-0">
//                       <div className="accordion-header" id="headingTwo">
//                         <a
//                           className="accordion-button p-2 shadow-none"
//                           data-bs-toggle="collapse"
//                           href="#collapseTwo"
//                           aria-expanded="false"
//                           aria-controls="collapseTwo"
//                         >
//                           <div className="d-flex align-items-center">
//                             <div className="flex-shrink-0 avatar-xs">
//                               <div className="avatar-title bg-success rounded-circle material-shadow">
//                                 <i className="mdi mdi-gift-outline"></i>
//                               </div>
//                             </div>
//                             <div className="flex-grow-1 ms-3">
//                               <h6 className="fs-15 mb-1 fw-semibold">
//                                 Paid -{" "}
//                                 <span className="fw-normal">
//                                   {order &&
//                                     new Date(
//                                       order.created_at
//                                     ).toLocaleDateString("en-US")}
//                                 </span>
//                               </h6>
//                             </div>
//                           </div>
//                         </a>
//                       </div>
//                       <div
//                         id="collapseTwo"
//                         className="accordion-collapse collapse show"
//                         aria-labelledby="headingTwo"
//                         data-bs-parent="#accordionExample"
//                       >
//                         <div className="accordion-body ms-2 ps-5 pt-0">
//                           <h6 className="mb-1">
//                             Paid an amount of $
//                             {order?.payment_info?.[0]?.amount_paid}.
//                           </h6>
//                           <p className="text-muted mb-0">
//                             {order?.payment_info?.[0]?.payment_date &&
//                               new Date(
//                                 order?.payment_info?.[0]?.payment_date
//                               ).toLocaleDateString("en-US")}
//                           </p>
//                         </div>
//                       </div>
//                     </div>
//                   ) : (
//                     ""
//                   )}
//                   {/* <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingThree">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseThree"
//                         aria-expanded="false"
//                         aria-controls="collapseThree"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-success rounded-circle material-shadow">
//                               <i className="ri-truck-line"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-15 mb-1 fw-semibold">
//                               Shipping -{" "}
//                               <span className="fw-normal">
//                                 Thu, 16 Dec 2021
//                               </span>
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                     <div
//                       id="collapseThree"
//                       className="accordion-collapse collapse show"
//                       aria-labelledby="headingThree"
//                       data-bs-parent="#accordionExample"
//                     >
//                       <div className="accordion-body ms-2 ps-5 pt-0">
//                         <h6 className="fs-14">
//                           RQK Logistics - MFDS1400457854
//                         </h6>
//                         <h6 className="mb-1">Your item has been shipped.</h6>
//                         <p className="text-muted mb-0">
//                           Sat, 18 Dec 2021 - 4.54PM
//                         </p>
//                       </div>
//                     </div>
//                   </div> */}
//                   {/* <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingFour">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseFour"
//                         aria-expanded="false"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-light text-success rounded-circle material-shadow">
//                               <i className="ri-takeaway-fill"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-14 mb-0 fw-semibold">
//                               Out For Delivery
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                   </div> */}
//                   {/* <div className="accordion-item border-0">
//                     <div className="accordion-header" id="headingFive">
//                       <a
//                         className="accordion-button p-2 shadow-none"
//                         data-bs-toggle="collapse"
//                         href="#collapseFile"
//                         aria-expanded="false"
//                       >
//                         <div className="d-flex align-items-center">
//                           <div className="flex-shrink-0 avatar-xs">
//                             <div className="avatar-title bg-light text-success rounded-circle material-shadow">
//                               <i className="mdi mdi-package-variant"></i>
//                             </div>
//                           </div>
//                           <div className="flex-grow-1 ms-3">
//                             <h6 className="fs-14 mb-0 fw-semibold">
//                               Delivered
//                             </h6>
//                           </div>
//                         </div>
//                       </a>
//                     </div>
//                   </div> */}
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>

//         <div className="col-xl-3">
//           {/* <div className="card">
//             <div className="card-header">
//               <div className="d-flex">
//                 <h5 className="card-title flex-grow-1 mb-0">
//                   <i className="mdi mdi-truck-fast-outline align-middle me-1 text-muted"></i>{" "}
//                   Logistics Details
//                 </h5>
//                 <div className="flex-shrink-0">
//                   <a
//                     href="javascript:void(0);"
//                     className="badge bg-primary-subtle text-primary fs-11"
//                   >
//                     Track Order
//                   </a>
//                 </div>
//               </div>
//             </div>
//             <div className="card-body">
//               <div className="text-center">
//                 <h5 className="fs-16 mt-2">RQK Logistics</h5>
//                 <p className="text-muted mb-0">ID: MFDS1400457854</p>
//                 <p className="text-muted mb-0">Payment Mode : Debit Card</p>
//               </div>
//             </div>
//           </div> */}

//           <div className="card">
//             <div className="card-header">
//               <div className="d-flex">
//                 <h5 className="card-title flex-grow-1 mb-0 table-headers">
//                   Customer Details
//                 </h5>
//               </div>
//             </div>
//             <div className="card-body">
//               <ul className="list-unstyled mb-0 vstack gap-3">
//                 <li>
//                   <div className="d-flex align-items-center">
//                     <div className="flex-grow-1 ms-3">
//                       <h6 className="fs-14 mb-1 table-cells">
//                         {order?.customer_first_name} {order?.customer_last_name}
//                       </h6>
//                       <p className="text-muted mb-0">Customer</p>
//                     </div>
//                   </div>
//                 </li>
//                 <li className="table-cells">
//                   <i className="ri-mail-line me-2 align-middle text-muted fs-16"></i>
//                   {order?.customer_email}
//                 </li>
//                 <li className="table-cells">
//                   <i className="ri-phone-line me-2 align-middle text-muted fs-16"></i>
//                   {order?.customer_phone}
//                 </li>
//               </ul>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <h5 className="card-title mb-0 table-headers">
//                 <i className="ri-map-pin-line align-middle me-1 text-muted"></i>{" "}
//                 Billing Address
//               </h5>
//             </div>
//             <div className="card-body">
//               <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
//                 <li className="fw-medium fs-14 table-cells">{order?.customer_name}</li>
//                 <li className="table-cells">{order?.billing_address?.phone}</li>
//                 <li className="table-cells">{order?.billing_address?.addressLine1}</li>
//                 <li className="table-cells">
//                   {order?.billing_address?.city},{" "}
//                   {order?.billing_address?.state} -{" "}
//                   {order?.shipping_address?.zip}
//                 </li>
//                 <li className="table-cells">{order?.billing_address?.country}</li>
//               </ul>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <h5 className="card-title mb-0 table-headers">
//                 <i className="ri-map-pin-line align-middle me-1 text-muted "></i>{" "}
//                 Shipping Address
//               </h5>
//             </div>
//             <div className="card-body">
//               <ul className="list-unstyled vstack gap-2 fs-13 mb-0">
//                 <li className="fw-medium fs-14 table-cells">{order?.customer_name}</li>
//                 <li className="table-cells">{order?.shipping_address?.phone}</li>
//                 <li className="table-cells">{order?.shipping_address?.addressLine1}</li>
//                 <li className="table-cells">
//                   {order?.shipping_address?.city},{" "}
//                   {order?.shipping_address?.state} -{" "}
//                   {order?.shipping_address?.zip}
//                 </li>
//                 <li className="table-cells">{order?.shipping_address?.country}</li>
//               </ul>
//             </div>
//           </div>

//           <div className="card">
//             <div className="card-header">
//               <h5 className="card-title mb-0 table-headers">
//                 <i className="ri-secure-payment-line align-bottom me-1 "></i>{" "}
//                 Payment Details
//               </h5>
//             </div>
//             <div className="card-body">
//               <div className="d-flex align-items-center mb-2">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0 table-cells">Transaction ID:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0 table-cells">
//                     {order?.payment_info?.[0]?.payment_id || ""}
//                   </h6>
//                 </div>
//               </div>
//               <div className="d-flex align-items-center mb-2">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0 table-cells">Payment Method:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0 table-cells">{order?.payment_info?.[0]?.method}</h6>
//                 </div>
//               </div>
//               {/* <div className="d-flex align-items-center mb-2">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0">Card Holder Name:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0">Joseph Parker</h6>
//                 </div>
//               </div> */}
//               <div className="d-flex align-items-center">
//                 <div className="flex-shrink-0">
//                   <p className=" mb-0 table-cells">Total Amount:</p>
//                 </div>
//                 <div className="flex-grow-1 ms-2">
//                   <h6 className="mb-0 table-cells">${order?.payment_info?.[0]?.total_amount}</h6>
//                 </div>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>
//       <div
//         className="card-body container"
//         style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
//       >
//         <OrderInvoice ref={invoiceRef} order={order} customer={customer} />
//       </div>
//     </div>
//   );
// };

// export default RealtorOrderDetails;